import React from "react";
import { CONTACT } from "./constants";
import {
  Ethnicity,
  PlatformUnits,
  ReferralStep,
  SmokerStatus,
  TattooStatus,
} from "./generated-api";
import { OpenTo } from "./generated-api/models/OpenTo";
import { PlatformLanguage } from "./generated-api/models/PlatformLanguage";

export type TranslationTree = {
  SHARED: {
    LABELS: {
      BACK_BUTTON: string;
      CONTINUE_BUTTON: string;
      SENDING_BUTTON: string;
      SAVE_BUTTON: string;
      CANCEL_BUTTON: string;
      EDIT_BUTTON: string;
      CONFIRM_BUTTON: string;
      DONE_BUTTON: string;
      CONTACT_BUTTON: string;
      REFRESH_BUTTON: string;
    };
    ERRORS: {
      PERSISTENT: string;
      TRY_AGAIN: string;
    };
  };
  LOGIN_OTP_CARD: {
    LABELS: {
      LOGIN_CODE_TITLE: string;
      CHECK_EMAIL: string;
      SIGN_IN: string;
      SIGNING_IN: string;
      RESEND_CODE: string;
      RESENDING: string;
    };
  };
  LOGIN_EMAIL_CARD: {
    LABELS: {
      COMPLETE_CAPTCHA: string;
      EMAIL: string;
      BUTTON_SENDING: string;
      BUTTON_CONTINUE: string;
      NO_ACCOUNT_QUESTION: string;
      HAVE_ACCOUNT_QUESTION: string;
      SIGN_UP: string;
      SIGN_IN: string;
      OR: string;
      CONTINUE_WITH_GOOGLE: string;
      CREATING_NEW_ACCOUNT: string;
      LAST_USED: string;
    };
    ERRORS: {
      USER_ALREADY_EXISTS: string;
      INVALID_EMAIL: string;
      PLEASE_ENTER_VALID: string;
      GOOGLE_NOT_SUPPORTED_TITLE: string;
      GOOGLE_NOT_SUPPORTED_DESCRIPTION: string;
    };
  };
  MENU_BAR: {
    EXPLORE: string;
    AI_MATCHES: string;
    SEARCH: string;
    FAVORITES: string;
    MESSAGES: string;
  };
  PROFILE_MENU: {
    PROFILE: string;
    ACCOUNT: string;
    SIGN_OUT: string;
  };
  ONBOARDING_TERMS: {
    LABELS: {
      TERMS_TITLE: string;
      TERMS_CONFIRMATION: React.ReactNode;
      AGE_CONFIRMATION: string;
      LANGUAGE_PREFERENCE: string;
      UNITS_PREFERENCE: string;
    };
    ERRORS: {
      ACCEPT_TERMS: string;
      TERMS_CONFIRMATION_REQUIRED: string;
      AGE_CONFIRMATION_REQUIRED: string;
    };
  };
  ONBOARDING_STEPS: {
    LABELS: {
      FORM: string;
      PHOTOS: string;
      FACE: string;
    };
  };
  GENDER_SELECT_CARD: {
    LABELS: {
      PROMPT: string;
      MALE: string;
      FEMALE: string;
    };
  };
  USER_FORM: {
    LABELS: {
      TITLE: string;
      OPTIONAL_DETAILS: string;
      DISPLAY_NAME: string;
      MAIN_LOCATION: string;
      DATE_OF_BIRTH: string;
      LANGUAGES: string;
      NATIONALITY: string;
      ETHNICITY: string;
      OPEN_TO: string;
      OPEN_TO_MIN: string;
      SECONDARY_LOCATION: string;
      HEIGHT: string;
      SMOKER: string;
      TATTOOS: string;
      DRINKING: string;
      YOUR_BIO: string;
      REFERRED_BY: string;
      CONTACT_INFO_TITLE: string;
      CONTACT_INFO_DESCRIPTION: string;
      UPGRADE_TO_EDIT: string;
      CONTACT_INFO_SUSPEND: string;
    };
    PLACEHOLDERS: {
      DISPLAY_NAME_FEMALE: string;
      DISPLAY_NAME_MALE: string;
      CITY_INPUT: string;
      LANGUAGE_INPUT: string;
      NATIONALITY_INPUT: string;
      ETHNICITY_INPUT: string;
      BIO_INPUT: string;
      REFERRED_BY_INPUT: string;
    };
    ENUM_BADGES: {
      [OpenTo.DATES]: string;
      [OpenTo.RELATIONSHIP]: string;
      [OpenTo.TRAVEL_TOGETHER]: string;
      [SmokerStatus.NO]: string; // same for DrinkingStatus
      [SmokerStatus.SOMETIMES]: string;
      [SmokerStatus.REGULAR]: string;
      [TattooStatus.NONE]: string;
      [TattooStatus.MINIMAL]: string;
      [TattooStatus.MODERATE]: string;
      [PlatformUnits.METRIC]: string;
      [PlatformUnits.US]: string;
    };
    ERRORS: {
      FORM_SUBMISSION: string;
      SECONDARY_LOCATION_DIFFERENT: string;
      CONTACT_INFO_NOT_ALLOWED_BIO: string;
    };
  };
  USER_FORM_SCHEMA_VALIDATION: {
    DISPLAY_NAME: {
      MIN: string;
      MAX: string;
      REGEX: string;
    };
    CITY: {
      MIN: string;
      MAX: string;
    };
    LANGUAGES: {
      MIN: string;
      MAX: string;
    };
    NATIONALITY: {
      MIN: string;
      MAX: string;
    };
    ETHNICITY: {
      MIN: string;
      MAX: string;
    };
    OPEN_TO: {
      MIN: string;
    };
    BADGES: {
      MIN: string;
      MAX: string;
    };
    BIO: {
      MIN: string;
      MAX: string;
    };
    REFERRED_BY: {
      MIN: string;
      MAX: string;
      REGEX: string;
    };
    TELEGRAM: {
      MIN: string;
      MAX: string;
      REGEX: string;
    };
    INSTAGRAM: {
      MIN: string;
      MAX: string;
      REGEX: string;
      DUPLICATE_PERIODS: string;
      START_PERIODS: string;
      END_PERIODS: string;
    };
    WHATSAPP: {
      MIN: string;
      MAX: string;
      REGEX: string;
    };
  };
  USER_FORM_ACTION_REQUIRED: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: string;
      NO_CONTACT_INFO: string;
      NO_OFFENSIVE_LANGUAGE: string;
    };
  };
  ETHNICITIES: {
    [Ethnicity.ASIAN]: string;
    [Ethnicity.BLACK_AFRICAN_DESCENT]: string;
    [Ethnicity.LATIN_HISPANIC]: string;
    [Ethnicity.MIDDLE_EASTERN]: string;
    [Ethnicity.MIXED]: string;
    [Ethnicity.SOUTH_ASIAN]: string;
    [Ethnicity.WHITE]: string;
    [Ethnicity.OTHER]: string;
  };
  PHOTO_UPLOAD_PAGE: {
    LABELS: {
      CARD_TITLE: string;
      DESCRIPTION: string;
      MORE_INFO_FACE: string;
      MORE_INFO_FORBIDDEN: React.ReactNode;
      MORE_INFO_TIP: React.ReactNode;
      CROP_MODAL_TILE: string;
      CROP_MODAL_WARNING: string;
    };
    ERRORS: {
      UPLOAD: string;
      UPLOADS: string;
      DELETE: string;
      HEIC_CONVERSION: string;
      LOW_RESOLUTION_TITLE: string;
      LOW_RESOLUTION_DESCRIPTION: (resolution: string) => React.ReactNode;
    };
  };
  PHOTO_UPLOAD_ACTION_REQUIRED: {
    TITLE: string;
    DESCRIPTION: string;
    NO_FACE: string;
    NO_FORBIDDEN: string;
  };
  PHOTO_UPLOAD_ACTION_REQUIRED_ELEGANT: {
    TITLE: string;
    DESCRIPTION: React.ReactNode;
    ELEGANT_PHOTOS: React.ReactNode;
    HIGH_QUALITY_PHOTOS: React.ReactNode;
    HAPPY_TO_REVIEW: string;
  };
  FACE_VERIFICATION: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: (showFigure: boolean) => string;
      MORE_INFO: string;
      STEP_ONE: string;
      STEP_TWO: string;
      PAGE_TWO_DESCRIPTION: string;
      RECORD_BUTTON: string;
      RETAKE_BUTTON: string;
      STOP_BUTTON: string;
      VERIFY_SAFARI: string;
    };
    ERRORS: {
      UPLOAD: string;
      EXISTING: string;
      CAMERA: (err: string) => string;
    };
  };
  FACE_VERIFICATION_ACTON_REQUIRED: {
    TITLE: string;
    DESCRIPTION: string;
    CLARITY: string;
    SAME_ON_PHOTOS: string;
  };
  APPLIED_PAGE: {
    TITLE: string;
    DESCRIPTION: string;
    SECTION_HEADER: string;
    FOLLOW_US_BUTTON: string;
    NO_FAKES_TITLE: string;
    NO_FAKES_DESCRIPTION: string;
    SUCCESSFUL_MEN_TITLE: string;
    SUCCESSFUL_MEN_DESCRIPTION: string;
    BEAUTIFUL_GIRLS_TITLE: string;
    BEAUTIFUL_GIRLS_DESCRIPTION: string;
    MESSAGE_INSTANTLY_TITLE: string;
    MESSAGE_INSTANTLY_DESCRIPTION: string;
    TIME_SAVING_TITLE: string;
    TIME_SAVING_DESCRIPTION: string;
  };
  APPLIED_DOORS_CLOSED: {
    TITLE: string;
    DESCRIPTION: string;
  };
  WAITLISTED_PAGE: {
    TITLE: string;
    DESCRIPTION: string;
  };
  DENIED_PAGE: {
    TITLE: string;
    DESCRIPTION: string;
  };
  BANNED_PAGE: {
    TITLE: string;
    REASON_PAYMENT: string;
    REASON_VIOLATION: string;
  };
  SEARCH_PAGE: {
    LABELS: {
      SEARCH_BUTTON: string;
      CLEAR_BUTTON: string;
      TITLE: string;
      TITLE_MOBILE: string;
      LOCATION: string;
      DISTANCE: string;
      AGE: string;
      LANGUAGES: string;
      NATIONALITY: string;
      ETHNICITY: string;
      HEIGHT: string;
      OPEN_TO: string;
      SMOKER: string;
      TATTOOS: string;
      DRINKING: string;
    };
    PLACEHOLDERS: {
      CITIES_INPUT: string;
      LANGUAGES_INPUT: string;
      NATIONALITY_INPUT: string;
      ETHNICITY_INPUT: string;
    };
    SORT_BY: {
      RECENTLY_ACTIVE: string;
      NEWEST: string;
    };
    ERRORS: {
      SEARCH: string;
      SEARCH_DESCRIPTION: string;
      NO_RESULTS: string;
      NO_RESULTS_DESCRIPTION: string;
    };
  };
  PHOTO_SHARING: {
    LABELS: {
      SHARE_SUCCESS: string;
      UNSHARE_SUCCESS: string;
      SHARED_MESSAGE: (name?: string) => string;
      REQUESTED_MESSAGE: (name?: string) => string;
      SHARE_BUTTON: string;
      UNSHARE_BUTTON: string;
      HAS_PRIVATE_PHOTOS: string;
      REQUEST_BUTTON: string;
      REQUESTED_BUTTON: string;
      NO_IMAGES_AVAILABLE: string;
    };
    ERRORS: {
      SHARE: string;
      UNSHARE: string;
      REQUESTING: string;
    };
  };
  PROFILE_VIEW: {
    LABELS: {
      BLOCK_BUTTON: string;
      REPORT_BUTTON: string;
      REPORT_DIALOG_TITLE: string;
      REPORT_DIALOG_DESCRIPTION: string;
      REPORT_USER_BUTTON: string;
      REPORT_SUCCESS: string;
      VIEW_CHAT_BUTTON: string;
      MESSAGE_BUTTON: string;
      GENERAL_SECTION: string;
      LANGUAGES_SECTION: string;
      OPEN_TO: string;
      SMOKER: string;
      TATTOOS: string;
      DRINKING: string;
      LANGUAGES: string;
      JOINED_ON: string;
    };
    ERRORS: {
      LOADING: string;
      LOADING_DESCRIPTION: string;
      BLOCK: string;
      REPORT: string;
    };
  };
  SELF_PROFILE_VIEW: {
    LABELS: {
      YOUR_PROFILE: string;
      EDIT_BUTTON: string;
      EDIT: string;
      EDIT_SUCCESS: string;
      VIEW_BUTTON: string;
    };
    ERRORS: {
      LOADING: string;
      LOADING_DESCRIPTION: string;
    };
  };
  MESSAGE_SHORTCUT: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: string;
      INPUT_PLACEHOLDER: string;
      CLEAR_BUTTON: string;
    };
    ERROR: {
      UPDATE: string;
    };
  };
  MESSAGE_DRAWER: {
    LABELS: {
      SEND_BUTTON: string;
      UPGRADE: string;
      UPGRADE_PLACEHOLDER: (name: string | null) => string;
      MESSAGE_PLACEHOLDER: (name: string | null) => string;
    };
    ERROR: {
      SEND: string;
      MEMBERSHIP: string;
      MESSAGE_EMPTY: string;
      MESSAGE_TOO_LONG: string;
    };
  };
  FAVORITES_PAGE: {
    LABELS: {
      FAVORITES: string;
      FAVORITED_ME: string;
      LAST_FAVORITED: string;
      RECENTLY_ACTIVE: string;
      MY_FAVORITES_EMPTY: string;
      MY_FAVORITES_EMPTY_DESCRIPTION: string;
      FAVORITED_ME_EMPTY: string;
      FAVORITED_ME_EMPTY_DESCRIPTION: string;
    };
    ERRORS: {
      LOADING: string;
      LOADING_DESCRIPTION: string;
      COULD_NOT_FAVORITE: string;
      COULD_NOT_UNFAVORITE: string;
    };
  };
  MESSAGES: {
    LABELS: {
      HIDDEN: string;
      NO_CONVERSATIONS_TITLE: string;
      NO_CONVERSATIONS_DESCRIPTION: string;
      ACTIVE: string;
      DELETE_BUTTON: string;
      UPGRADE_TO_READ_MESSAGE: string;
      MESSAGE: string;
      COPY_BUTTON: string;
      CONFIGURE_CONTACTS: string;
      SELECT_TO_START: string;
    };
    ERRORS: {
      DELETE: string;
      DELETE_CONVERSATION: string;
      SEND: string;
      LOADING: string;
      LOADING_DESCRIPTION: string;
    };
  };
  EXPLORE_PAGE: {
    LABELS: {
      EMPTY_TITLE: string;
      EMPTY_DESCRIPTION: string;
    };
    ERRORS: {
      LOADING: string;
      LOADING_DESCRIPTION: string;
      REFRESH: string;
    };
  };
  ACCOUNT_SETTINGS: {
    LABELS: {
      REFERRAL_CARD_TITLE: string;
      REFERRAL_CARD_DESCRIPTION: string;
      UPDATED_SUCCESS: string;
      GENERAL_SETTINGS_SECTION_TITLE: string;
      NOTIFICATIONS_SECTION_TITLE: string;
      EMAIL_NOTIFICATIONS: string;
      LANGUAGE_SETTING: string;
      UNITS_SETTING: string;
    };
    ERRORS: {
      LOADING_TITLE: string;
      LOADING_DESCRIPTION: string;
      UPDATED_ERROR: string;
    };
  };
  MEMBERSHIP_SETTINGS: {
    LABELS: {
      FREE_PREVIEW_TITLE: string;
      FREE_PREVIEW_DESCRIPTION: React.ReactNode;
      FULL_MEMBER_TITLE: string;
      FULL_MEMBER_DESCRIPTION: (date: string) => React.ReactNode;
      FULL_MEMBER_RECURRING_DESCRIPTION: (date: string) => React.ReactNode;
      FULL_MEMBER_CANCELLED_DESCRIPTION: (date: string) => React.ReactNode;
      MEMBERSHIP_ENDED_TITLE: string;
      MEMBERSHIP_ENDED_DESCRIPTION: (date: string) => React.ReactNode;
      YOUR_MEMBERSHIP_TITLE: string;
      EXTEND_BUTTON: string;
      UPGRADE_BUTTON: string;
      ENDING_IN: string;
      CRYPTO_INSTRUCTIONS: React.ReactNode;
      CANCEL_SUBSCRIPTION: string;
      REMAIN_ACTIVE_UNTIL: string;
      NO_RENEWAL: string;
      CANCELED_SUCCESS: string;
      MANAGE_SUBSCRIPTION: string;
    };
    ERRORS: {
      MEMBERSHIP_LOADING: string;
      SUBSCRIPTION_CANCELED: string;
    };
  };
  EMAIL_SETTINGS: {
    LABELS: {
      UPDATE_EMAIL_TITLE: string;
      SEND_CODE_BUTTON: string;
      ONE_TIME_CODE: string;
      EMAILED_CODE_SUCCESS: string;
      EMAIL_UPDATED_SUCCESS: string;
      NEW_EMAIL_PLACEHOLDER: string;
    };
    ERRORS: {
      EMAIL_UPDATE_CONFLICT: string;
      EMAIL_UPDATE_INVALID: string;
      EMAIL_UPDATE_ERROR: string;
    };
  };
  APP_INSTALL_DIALOG: {
    LABELS: {
      DIALOG_TITLE: string;
      APP_EXPERIENCE: string;
      NOTIFICATIONS: string;
      SAVED_LOGIN: string;
      INSTRUCTIONS_BUTTON: string;
    };
  };
  APP_RELOAD_CARD: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: string;
    };
  };
  SETTINGS_FEEDBACK_CARD: {
    LABELS: {
      TITLE: string;
      MESSAGE_FOUNDERS_BUTTON: string;
      EMAIL_US: string;
      DELETE_ACCOUNT_BUTTON: string;
      DELETE_ACCOUNT_TITLE: string;
      DELETE_ACCOUNT_DESCRIPTION: string;
      DELETE_ACCOUNT_CONFIRM: string;
    };
    ERRORS: {
      DELETE_ACCOUNT: string;
    };
  };
  INSTALL_APP_INSTRUCTIONS: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: string;
      IOS_STEP_1: string;
      IOS_STEP_2: string;
      IOS_STEP_3: string;
      IOS_26_STEP_1: string;
      IOS_26_STEP_2: string;
      IOS_26_STEP_3: string;
      IOS_26_STEP_4: string;
      ANDROID_STEP_1: string;
      ANDROID_STEP_2: string;
      ANDROID_STEP_2_DEBUG: string;
      ANDROID_STEP_2_BUTTON: string;
      ANDROID_STEP_3: string;
      ANDROID_TIPS_TITLE: string;
      ANDROID_TIPS_DESCRIPTION: string;
      ANDROID_TIPS_LIST: React.ReactNode;
    };
  };
  REFERRAL_PAGE: {
    LABELS: {
      TITLE: string;
      SHARE_YOUR_CODE: string;
      REFERRALS: string;
      FEMALE_REMAINING_REWARDS_TITLE: string;
      MALE_EXPIRY_DATE_TITLE: string;
      MALE_REWARD: string;
      MALE_EXPIRY_DATE: string;
      MALE_REFERRAL: string;
      FEMALE_REFERRAL: string;
      EARNINGS: string;
      FEMALE_REWARD: string;
      CLAIMING_TITLE: string;
      CLAIMING_INSTRUCTIONS: string;
    };
  };
  REFER_AND_EARN: {
    LABELS: {
      BUTTON_TITLE: string;
      CARD_TITLE: string;
      DESCRIPTION: string;
      NO_GUARANTEE: string;
      SEE_HOW_BUTTON: string;
    };
  };
  REFERRAL_LIST: {
    LABELS: {
      MY_REFERRALS: string;
      NO_REFERRALS_TITLE: string;
      NO_REFERRALS_DESCRIPTION: string;
    };
    ERRORS: {
      LOADING_TITLE: string;
      LOADING_DESCRIPTION: string;
    };
  };
  REFERRAL_STEPS: {
    [ReferralStep.APPLYING]: string;
    [ReferralStep.APPLIED]: string;
    [ReferralStep.APPROVED]: string;
    [ReferralStep.DENIED]: string;
    [ReferralStep.SUBSCRIBED]: string;
  };
  ERROR_PAGE: {
    LABELS: {
      TITLE: string;
      GO_BACK: string;
      TRY_AGAIN: string;
    };
  };
  NOTIFICATIONS_DIALOG: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: string;
      DESCRIPTION_APPLIED: string;
      ENABLE_BUTTON: string;
      DISMISS_BUTTON: string;
      DONT_SHOW_BUTTON: string;
    };
  };
  PUSH_NOTIFICATIONS_SETTINGS: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: string;
      ENABLED_STATUS: string;
      DISABLE_BUTTON: string;
      ENABLE_BUTTON: string;
      DISABLED_STATUS: string;
    };
  };
  SELECT_PLAN_CARD: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: string;
      MONTH: string;
      WEEK: string;
      MONTHS: string;
      MONTHLY: string;
      CARD: string;
      CRYPTO: string;
      ONE_TIME: string;
      RECURRING: string;
      ONE_TIME_PAYMENT: string;
      RECURRING_PAYMENT: string;
      INFO_HAVE_EXISTING_UNTIL: string;
      INFO_SWITCHING_IMMEDIATE: string;
      PAY: string;
      PAY_WITH_CRYPTO: string;
      AGREE_TO_TERMS: React.ReactNode;
      AGREE_TO_TERMS_AND_PRIVACY: React.ReactNode;
      RESTORE_PURCHASES: string;
      ACTIVATE: string;
      TRIAL_PLAN_NAME: string;
      TRIAL_FREE: string;
      TRIAL_DIALOG_TITLE: string;
      TRIAL_DIALOG_DESCRIPTION: string;
      TRIAL_ACTIVATED_SUCCESS: string;
      TRIAL_ACTIVATION_FAILED: string;
    };
    ERRORS: {
      LOADING: string;
      NONE_AVAILABLE: string;
    };
  };
  TRIAL_BANNER: {
    LABELS: {
      GIFTED: string;
      ACTIVATE: string;
    };
  };
  PAYMENT_CONFIRMATION_DIALOG: {
    LABELS: {
      TITLE: string;
      DESCRIPTION: string;
      PAID_BUTTON: string;
      NOT_NOW_BUTTON: string;
      SUCCESS_MESSAGE: string;
      FAILURE_MESSAGE: string;
      FAILURE_DESCRIPTION: string;
      PURCHASES_RESTORED: string;
      NO_PREVIOUS_PURCHASE: string;
      FAILED_TO_CHECK_PURCHASE: string;
      PURCHASE_PROCESSING_MESSAGE: string;
      PURCHASE_PROCESSING_DESCRIPTION: string;
    };
  };
  OFFBOARDING_FORM: {
    LABELS: {
      WHY_LEAVING_TITLE: string;
      CHOOSE_REASON_DESCRIPTION: string;
      FOUND_ON_PULSE: string;
      FOUND_ELSEWHERE: string;
      TAKING_BREAK: string;
      NOT_ENOUGH_MESSAGES: string;
      TOO_EXPENSIVE: string;
      OTHER: string;
      MEMBERS_GROWING_TITLE: string;
      MEMBERS_GROWING_DESCRIPTION_FEMALE: string;
      MEMBERS_GROWING_DESCRIPTION_MALE: string;
      CONTINUE_DELETING_BUTTON: string;
      MEMBERS_GETTING_VALUE_TITLE: string;
      MEMBERS_GETTING_VALUE_DESCRIPTION: string;
      IN_A_FEW_WORDS_WHY_TITLE: string;
      MIN_TEN_CHARS_PLACEHOLDER: string;
    };
  };
};

export const TRANSLATIONS: Record<PlatformLanguage, TranslationTree> = {
  [PlatformLanguage.ENGLISH]: {
    SHARED: {
      LABELS: {
        BACK_BUTTON: "Back",
        CONTINUE_BUTTON: "Continue",
        SENDING_BUTTON: "Sending...",
        SAVE_BUTTON: "Save",
        CANCEL_BUTTON: "Cancel",
        EDIT_BUTTON: "Edit",
        CONFIRM_BUTTON: "Confirm",
        DONE_BUTTON: "Done",
        CONTACT_BUTTON: "Contact",
        REFRESH_BUTTON: "Refresh",
      },
      ERRORS: {
        PERSISTENT: `Please adjust your VPN or try again later. If it persists contact ${CONTACT.SUPPORT_EMAIL}`,
        TRY_AGAIN: "An error occurred. Please try again.",
      },
    },
    LOGIN_OTP_CARD: {
      LABELS: {
        LOGIN_CODE_TITLE: "Login code",
        CHECK_EMAIL: "Check your email for a one-time code.",
        SIGN_IN: "Sign in",
        SIGNING_IN: "Signing in...",
        RESEND_CODE: "Resend code",
        RESENDING: "Resending...",
      },
    },
    LOGIN_EMAIL_CARD: {
      LABELS: {
        COMPLETE_CAPTCHA: "Please complete the captcha.",
        EMAIL: "Email",
        BUTTON_SENDING: "Sending...",
        BUTTON_CONTINUE: "Continue",
        NO_ACCOUNT_QUESTION: "Don't have an account? ",
        HAVE_ACCOUNT_QUESTION: "Already have an account? ",
        SIGN_UP: "Sign up",
        SIGN_IN: "Sign in",
        OR: "or",
        CONTINUE_WITH_GOOGLE: "Continue with Google",
        CREATING_NEW_ACCOUNT: "New email detected, sign up to continue",
        LAST_USED: "Last used",
      },
      ERRORS: {
        USER_ALREADY_EXISTS: "User already exists. Please sign in.",
        INVALID_EMAIL: "Invalid email address.",
        PLEASE_ENTER_VALID: "Please enter a valid email address.",
        GOOGLE_NOT_SUPPORTED_TITLE: "Google Login Not Supported",
        GOOGLE_NOT_SUPPORTED_DESCRIPTION:
          "Yandex on iOS does not support Google login, use Safari, Chrome, or a different method.",
      },
    },
    MENU_BAR: {
      EXPLORE: "Explore",
      AI_MATCHES: "AI Matches",
      SEARCH: "Search",
      FAVORITES: "Favorites",
      MESSAGES: "Messages",
    },
    PROFILE_MENU: {
      PROFILE: "Profile",
      ACCOUNT: "Account",
      SIGN_OUT: "Sign out",
    },
    ONBOARDING_TERMS: {
      LABELS: {
        TERMS_TITLE: "Terms",
        TERMS_CONFIRMATION: (
          <>
            I accept the{" "}
            <a
              href="https://pulse-date.com/terms"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href="https://pulse-date.com/privacy"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              privacy policy
            </a>
          </>
        ),
        AGE_CONFIRMATION: "I am 18 years old or older",
        LANGUAGE_PREFERENCE: "Language",
        UNITS_PREFERENCE: "Units",
      },
      ERRORS: {
        ACCEPT_TERMS: "Error accepting terms and setting preferences",
        TERMS_CONFIRMATION_REQUIRED: "You must accept the terms and conditions",
        AGE_CONFIRMATION_REQUIRED: "You must confirm that you are 18 or older",
      },
    },
    ONBOARDING_STEPS: {
      LABELS: {
        FORM: "Form",
        PHOTOS: "Photos",
        FACE: "Face",
      },
    },
    GENDER_SELECT_CARD: {
      LABELS: {
        PROMPT: "You are...",
        MALE: "Male",
        FEMALE: "Female",
      },
    },
    USER_FORM: {
      LABELS: {
        TITLE: "Your details",
        OPTIONAL_DETAILS: "Optional Details",
        DISPLAY_NAME: "Display Name",
        MAIN_LOCATION: "Main Location",
        DATE_OF_BIRTH: "Date of Birth",
        LANGUAGES: "Languages",
        NATIONALITY: "Nationality",
        ETHNICITY: "Ethnicity",
        OPEN_TO: "Open To",
        OPEN_TO_MIN: "Min 1",
        SECONDARY_LOCATION: "Secondary Location",
        HEIGHT: "Height",
        SMOKER: "Smoker",
        TATTOOS: "Tattoos",
        DRINKING: "Drinking",
        YOUR_BIO: "Your Bio",
        REFERRED_BY: "Referred By",
        CONTACT_INFO_TITLE: "Contact Info",
        CONTACT_INFO_DESCRIPTION:
          "These optional fields will stay private until you share them with other members. You'll be able to send them in one-tap.",
        UPGRADE_TO_EDIT: "Upgrade to edit",
        CONTACT_INFO_SUSPEND:
          "Contact info in your bio will suspend your account",
      },
      PLACEHOLDERS: {
        DISPLAY_NAME_FEMALE: "Alina123",
        DISPLAY_NAME_MALE: "John123",
        CITY_INPUT: "Dubai",
        LANGUAGE_INPUT: "English",
        NATIONALITY_INPUT: "American",
        ETHNICITY_INPUT: "Select",
        BIO_INPUT:
          "30-250 characters, no contact info allowed. Something about you or what you're looking for.",
        REFERRED_BY_INPUT: "Code or email",
      },
      ENUM_BADGES: {
        [OpenTo.DATES]: "Dates",
        [OpenTo.RELATIONSHIP]: "Relationship",
        [OpenTo.TRAVEL_TOGETHER]: "Travel Together",
        [SmokerStatus.NO]: "No",
        [SmokerStatus.SOMETIMES]: "Sometimes",
        [SmokerStatus.REGULAR]: "Regular",
        [TattooStatus.NONE]: "None",
        [TattooStatus.MINIMAL]: "Minimal",
        [TattooStatus.MODERATE]: "Moderate",
        [PlatformUnits.METRIC]: "cm/km",
        [PlatformUnits.US]: "ft/miles",
      },
      ERRORS: {
        FORM_SUBMISSION: "Error submitting form",
        SECONDARY_LOCATION_DIFFERENT: "Must be different from main location",
        CONTACT_INFO_NOT_ALLOWED_BIO: "Contact info is not allowed in bio",
      },
    },
    USER_FORM_ACTION_REQUIRED: {
      LABELS: {
        TITLE: "Action Required",
        DESCRIPTION:
          "We reviewed your application, but need you to update the form following our guidelines:",
        NO_CONTACT_INFO:
          "No contact info or links are allowed in your bio or name",
        NO_OFFENSIVE_LANGUAGE:
          "No offensive language is allowed in your name or bio",
      },
    },
    USER_FORM_SCHEMA_VALIDATION: {
      DISPLAY_NAME: {
        MIN: "Minimum 3 characters",
        MAX: "Maximum 20 characters",
        REGEX:
          "Only letters, numbers and periods. No spaces, or special characters.",
      },
      CITY: {
        MIN: "Select your city",
        MAX: "Select at most one city",
      },
      LANGUAGES: {
        MIN: "Select at least one language",
        MAX: "Select at most 5 languages",
      },
      NATIONALITY: {
        MIN: "Select your nationality",
        MAX: "Select at most one nationality",
      },
      ETHNICITY: {
        MIN: "Select your ethinicity",
        MAX: "Select at most one ethinicity",
      },
      OPEN_TO: {
        MIN: "Select at least one",
      },
      BADGES: {
        MIN: "Select at least one",
        MAX: "Select at most one",
      },
      BIO: {
        MIN: "Minimum 30 characters",
        MAX: "Maximum 250 characters",
      },
      REFERRED_BY: {
        MIN: "Minimum 3 characters",
        MAX: "Maximum 20 characters",
        REGEX:
          "Only letters, numbers, and email characters are allowed. No spaces.",
      },
      TELEGRAM: {
        MIN: "Minimum 3 characters",
        MAX: "Maximum 40 characters",
        REGEX: "Only letters, numbers, and underscores are allowed",
      },
      INSTAGRAM: {
        MIN: "Minimum 1 character",
        MAX: "Maximum 30 characters",
        REGEX: "Only letters, numbers, and underscores are allowed",
        DUPLICATE_PERIODS: "Periods cannot appear consecutively",
        START_PERIODS: "Username cannot start with a period",
        END_PERIODS: "Username cannot end with a period",
      },
      WHATSAPP: {
        MIN: "Phone number must be at least 6 digits",
        MAX: "Phone number can't be longer than 20 digits",
        REGEX: "Can only contain digits and start with a '+'",
      },
    },
    ETHNICITIES: {
      [Ethnicity.ASIAN]: "Asian",
      [Ethnicity.BLACK_AFRICAN_DESCENT]: "Black / African Descent",
      [Ethnicity.LATIN_HISPANIC]: "Latin / Hispanic",
      [Ethnicity.MIDDLE_EASTERN]: "Middle Eastern",
      [Ethnicity.MIXED]: "Mixed",
      [Ethnicity.SOUTH_ASIAN]: "South Asian",
      [Ethnicity.WHITE]: "White",
      [Ethnicity.OTHER]: "Other",
    },
    PHOTO_UPLOAD_PAGE: {
      LABELS: {
        CARD_TITLE: "Profile Photos",
        DESCRIPTION:
          "Add at least 3 photos. The last 3 private photos are optional, and can only be seen if you share them.",
        MORE_INFO_FACE:
          "At least one photo should be a clear photo of your face, it may be a private photo.",
        MORE_INFO_FORBIDDEN: (
          <>
            <strong>No</strong> group photos, nudity, contact information or
            offensive content.
          </>
        ),
        MORE_INFO_TIP: (
          <>
            <strong>Tip:</strong> Upload a variety of photos to increase your
            chances of getting noticed.
          </>
        ),
        CROP_MODAL_TILE: "Crop Image",
        CROP_MODAL_WARNING: "No nudity or contact info allowed.",
      },
      ERRORS: {
        UPLOAD: "Error uploading photo",
        UPLOADS: "Error completing uploads",
        DELETE: "Error deleting photo",
        HEIC_CONVERSION:
          "Error converting HEIC/HEIF image. Please try another image.",
        LOW_RESOLUTION_TITLE: "Low Resolution Image",
        LOW_RESOLUTION_DESCRIPTION: (resolution: string) => (
          <>
            Please choose an image with a minimum resolution of{" "}
            <strong>{resolution}</strong> pixels. We recommend selecting a photo
            from your camera roll rather than using a screenshot, as screenshots
            are often lower in quality.
          </>
        ),
      },
    },
    PHOTO_UPLOAD_ACTION_REQUIRED: {
      TITLE: "Action Required",
      DESCRIPTION:
        "We reviewed your application, but need you to update your photos following our guidelines:",
      NO_FACE:
        "At least one photo should be a clear photo of your face, it may be a private photo",
      NO_FORBIDDEN:
        "No group photos, nudity, contact info or offensive content",
    },
    PHOTO_UPLOAD_ACTION_REQUIRED_ELEGANT: {
      TITLE: "Action Required",
      DESCRIPTION: (
        <>
          We see <b>strong potential</b> in your profile, but one or more of
          your <b>first 3</b> photos needs an update. We kindly invite you to
          update your photos with:
        </>
      ),
      ELEGANT_PHOTOS: (
        <>
          <b className="text-white">an elegant and refined style</b> - Pulse
          spotlights sophistication and elegance over sporty or casual looks.
        </>
      ),
      HIGH_QUALITY_PHOTOS: (
        <>
          <b className="text-white">high quality</b> - photos should be good
          resolution and sharp (no bathroom selfies, dirty mirrors, etc.)
        </>
      ),
      HAPPY_TO_REVIEW: "We are happy to review again.",
    },
    FACE_VERIFICATION: {
      LABELS: {
        TITLE: "Face Verification",
        DESCRIPTION: (
          showFigure: boolean,
        ) => `You will be asked to record a short video. 
        Please show your face ${
          showFigure ? "and yourself " : ""
        } as shown below.`,
        MORE_INFO: `You can return anytime to complete this step. 
        Ensure you're in a well-lit space.
        All members are required to verify their face and liveness.`,
        STEP_ONE: "1. Show your face",
        STEP_TWO: "2. Show yourself",
        PAGE_TWO_DESCRIPTION: "Record and rotate your head.",
        RECORD_BUTTON: "Record",
        RETAKE_BUTTON: "Retake",
        STOP_BUTTON: "Stop",
        VERIFY_SAFARI:
          "Can't see the camera? Open in Safari to verify then return to the app.",
      },
      ERRORS: {
        UPLOAD: "Upload failed",
        EXISTING: "Using existing video failed",
        CAMERA: (err: string) =>
          `Camera error: ${err}. Please check your camera permissions.`,
      },
    },
    FACE_VERIFICATION_ACTON_REQUIRED: {
      TITLE: "Action Required",
      DESCRIPTION:
        "We reviewed your application, but need you to resubmit your face:",
      CLARITY: "Make sure your face can be seen clearly",
      SAME_ON_PHOTOS: "Make sure you are the one on your photos",
    },
    APPLIED_PAGE: {
      TITLE: "Your application has been submitted",
      DESCRIPTION:
        "Our team will review your application. If approved, we'll be in touch by email within the next three days.",
      SECTION_HEADER: "What to Expect",
      FOLLOW_US_BUTTON: "Follow Us",
      NO_FAKES_TITLE: "No fakes",
      NO_FAKES_DESCRIPTION:
        "Face verification is required for everyone to join the platform. If someone has messaged you, they have been verified.",
      SUCCESSFUL_MEN_TITLE: "Successful men",
      SUCCESSFUL_MEN_DESCRIPTION:
        "Men pay several hundreds of dollars per month for membership. This means only men who can afford the membership can message you.",
      BEAUTIFUL_GIRLS_TITLE: "Beautiful girls",
      BEAUTIFUL_GIRLS_DESCRIPTION:
        "We review every applicant to ensure we keep a high standard for you, thereby saving you time and effort to find a good match.",
      MESSAGE_INSTANTLY_TITLE: "Message instantly",
      MESSAGE_INSTANTLY_DESCRIPTION:
        "Swiping is slow and exhausting. On Pulse you can start chatting with other members right away and share your contact info in one tap.",
      TIME_SAVING_TITLE: "Time-saving, easy filters",
      TIME_SAVING_DESCRIPTION:
        "Search by multiple locations at once, nationality, languages, and other filters not available on any other dating apps. All in a few clicks.",
    },
    APPLIED_DOORS_CLOSED: {
      TITLE: "Your application has been submitted",
      DESCRIPTION:
        "We're assembling our founding members thoughtfully. You'll hear from us when the first group is ready to begin.",
    },
    WAITLISTED_PAGE: {
      TITLE: "You've been waitlisted",
      DESCRIPTION:
        "We've reviewed your application and added you to our waitlist. As new spots open, we'll revisit your application and reach out by email if we're able to offer you access.",
    },
    DENIED_PAGE: {
      TITLE: "You're invited to reapply",
      DESCRIPTION: `Pulse is highly selective, and spaces are limited. 
                    Most members don't get in the first time. While we can't
                    offer access right now, this may change. You're welcome to
                    re-apply 180 days from the time of submitting your application.`,
    },
    BANNED_PAGE: {
      TITLE: "Your account has been banned.",
      REASON_PAYMENT: `We suspended your account due to a payment issue. If you wish to re-enable your account, you can do so by paying your outstanding balance. Please contact ${CONTACT.SUPPORT_EMAIL} for instructions.`,
      REASON_VIOLATION:
        "Your account was suspended due to not following our platform's terms and conditions.",
    },
    SEARCH_PAGE: {
      LABELS: {
        SEARCH_BUTTON: "Search",
        CLEAR_BUTTON: "Clear",
        TITLE: "Filters",
        TITLE_MOBILE: "Search Filters",
        LOCATION: "Location",
        DISTANCE: "Distance",
        AGE: "Age",
        LANGUAGES: "Languages",
        NATIONALITY: "Nationality",
        ETHNICITY: "Ethnicity",
        HEIGHT: "Height",
        OPEN_TO: "Open To",
        SMOKER: "Smoker",
        TATTOOS: "Tattoos",
        DRINKING: "Drinking",
      },
      PLACEHOLDERS: {
        CITIES_INPUT: "Dubai",
        LANGUAGES_INPUT: "Speaks at least one of",
        NATIONALITY_INPUT: "Swiss",
        ETHNICITY_INPUT: "Select",
      },
      SORT_BY: {
        RECENTLY_ACTIVE: "Recently Active",
        NEWEST: "Newest",
      },
      ERRORS: {
        SEARCH: "Error searching profiles",
        SEARCH_DESCRIPTION:
          "There was a problem searching. Please adjust your VPN or try again later.",
        NO_RESULTS: "No results for your search",
        NO_RESULTS_DESCRIPTION: "Try adjusting your filters.",
      },
    },
    PHOTO_SHARING: {
      LABELS: {
        SHARE_SUCCESS: "Shared private photos",
        UNSHARE_SUCCESS: "Unshared private photos",
        SHARED_MESSAGE: (name) => `${name ?? "You"} shared private photos`,
        REQUESTED_MESSAGE: (name) =>
          `${name ?? "You"} requested private photos`,
        SHARE_BUTTON: "Share",
        UNSHARE_BUTTON: "Unshare",
        HAS_PRIVATE_PHOTOS: "This user has private photos.",
        REQUEST_BUTTON: "Request",
        REQUESTED_BUTTON: "Requested",
        NO_IMAGES_AVAILABLE: "No images available",
      },
      ERRORS: {
        SHARE: "Error sharing photos",
        UNSHARE: "Error unsharing photos",
        REQUESTING: "Error requesting photos",
      },
    },
    PROFILE_VIEW: {
      LABELS: {
        BLOCK_BUTTON: "Block",
        REPORT_BUTTON: "Report",
        REPORT_DIALOG_TITLE: "Report user",
        REPORT_DIALOG_DESCRIPTION:
          "Please include a reason why you are reporting the user.",
        REPORT_USER_BUTTON: "Report user",
        REPORT_SUCCESS: "Reported user",
        VIEW_CHAT_BUTTON: "View Chat",
        MESSAGE_BUTTON: "Message",
        GENERAL_SECTION: "General",
        LANGUAGES_SECTION: "Languages",
        OPEN_TO: "Open To",
        SMOKER: "Smoker",
        TATTOOS: "Tattoos",
        DRINKING: "Drinking",
        LANGUAGES: "Languages",
        JOINED_ON: "Joined on",
      },
      ERRORS: {
        LOADING: "Error loading profile",
        LOADING_DESCRIPTION:
          "There was a problem loading the profile. Please adjust your VPN or try again later.",
        BLOCK: "Error blocking user",
        REPORT: "Error reporting user",
      },
    },
    SELF_PROFILE_VIEW: {
      LABELS: {
        YOUR_PROFILE: "My Profile",
        EDIT_BUTTON: "Edit",
        EDIT: "Edit Profile",
        EDIT_SUCCESS: "Profile updated",
        VIEW_BUTTON: "View",
      },
      ERRORS: {
        LOADING: "Error loading profile",
        LOADING_DESCRIPTION:
          "There was a problem loading the profile. Please adjust your VPN or try again later.",
      },
    },
    MESSAGE_SHORTCUT: {
      LABELS: {
        TITLE: "Message Shortcut",
        DESCRIPTION: "Configure a short message for easy sending in one-tap.",
        INPUT_PLACEHOLDER: "Type a message (max 120 characters)",
        CLEAR_BUTTON: "Clear",
      },
      ERROR: {
        UPDATE: "Could not update message shortcut",
      },
    },
    MESSAGE_DRAWER: {
      LABELS: {
        SEND_BUTTON: "Send",
        UPGRADE: "Upgrade",
        UPGRADE_PLACEHOLDER: (name) => `Upgrade to message ${name}...`,
        MESSAGE_PLACEHOLDER: (name) => `Message ${name}...`,
      },
      ERROR: {
        SEND: "Could not message user",
        MEMBERSHIP:
          "There was a problem loading your membership. Please adjust your VPN or try again later.",
        MESSAGE_EMPTY: "Message cannot be empty",
        MESSAGE_TOO_LONG: "Message cannot exceed 250 characters",
      },
    },
    FAVORITES_PAGE: {
      LABELS: {
        FAVORITES: "Favorites",
        FAVORITED_ME: "Favorited Me",
        LAST_FAVORITED: "Last Favorited",
        RECENTLY_ACTIVE: "Recently Active",
        MY_FAVORITES_EMPTY: "No favorites yet",
        MY_FAVORITES_EMPTY_DESCRIPTION:
          "When you favorite profiles, they'll appear here for easy access.",
        FAVORITED_ME_EMPTY: "No one has favorited you yet",
        FAVORITED_ME_EMPTY_DESCRIPTION:
          "When someone favorites you, they'll appear here.",
      },
      ERRORS: {
        LOADING: "Error loading favorites",
        LOADING_DESCRIPTION:
          "There was a problem loading your favorites. Please adjust your VPN or try again later.",
        COULD_NOT_FAVORITE: "Could not favorite user",
        COULD_NOT_UNFAVORITE: "Could not unfavorite user",
      },
    },
    MESSAGES: {
      LABELS: {
        HIDDEN: "Hidden",
        NO_CONVERSATIONS_TITLE: "No conversations",
        NO_CONVERSATIONS_DESCRIPTION: "You have no conversations yet.",
        ACTIVE: "active",
        DELETE_BUTTON: "Delete",
        UPGRADE_TO_READ_MESSAGE: "Upgrade to read and message",
        MESSAGE: "Message",
        COPY_BUTTON: "Copy",
        CONFIGURE_CONTACTS: "Configure Contacts",
        SELECT_TO_START: "Select a conversation to start messaging",
      },
      ERRORS: {
        DELETE: "Error deleting message",
        DELETE_CONVERSATION: "Error deleting conversation",
        SEND: "Could not message user",
        LOADING: "Error loading messages",
        LOADING_DESCRIPTION:
          "There was a problem loading your messages. Please adjust your VPN or try again later.",
      },
    },
    EXPLORE_PAGE: {
      LABELS: {
        EMPTY_TITLE: "No images to explore",
        EMPTY_DESCRIPTION:
          "Check back soon to discover new content from the community.",
      },
      ERRORS: {
        LOADING: "Error loading explore feed",
        LOADING_DESCRIPTION:
          "There was a problem loading your feed. Please adjust your VPN or try again later.",
        REFRESH: "Error refreshing feed",
      },
    },
    ACCOUNT_SETTINGS: {
      LABELS: {
        REFERRAL_CARD_TITLE: "Invite someone special",
        REFERRAL_CARD_DESCRIPTION:
          "Use the link below to invite someone who appreciates quality over quantity.",
        UPDATED_SUCCESS: "Updated account settings",
        GENERAL_SETTINGS_SECTION_TITLE: "Account Settings",
        NOTIFICATIONS_SECTION_TITLE: "Email",
        EMAIL_NOTIFICATIONS: "New message notifications",
        LANGUAGE_SETTING: "Language",
        UNITS_SETTING: "Units",
      },
      ERRORS: {
        LOADING_TITLE: "Error loading account settings",
        LOADING_DESCRIPTION:
          "There was a problem loading your account settings. Please adjust your VPN or try again later.",
        UPDATED_ERROR: "Error updating account settings",
      },
    },
    MEMBERSHIP_SETTINGS: {
      LABELS: {
        FREE_PREVIEW_TITLE: "Free preview",
        FREE_PREVIEW_DESCRIPTION: (
          <>
            Upgrade to <strong>full membership</strong> to start messaging other
            members.
          </>
        ),
        FULL_MEMBER_TITLE: "Full member",
        FULL_MEMBER_DESCRIPTION: (date) => (
          <>
            Your plan will end on <strong>{date}</strong>.
          </>
        ),
        FULL_MEMBER_RECURRING_DESCRIPTION: (date) => (
          <>
            Your plan will renew on <strong>{date}</strong>.
          </>
        ),
        FULL_MEMBER_CANCELLED_DESCRIPTION: (date) => (
          <>
            Your plan will end on <strong>{date}</strong>.
          </>
        ),
        MEMBERSHIP_ENDED_TITLE: "Ended",
        MEMBERSHIP_ENDED_DESCRIPTION: (date) => (
          <>
            Your plan ended on <strong>{date}</strong>. Upgrade to continue
            messaging other members.
          </>
        ),
        YOUR_MEMBERSHIP_TITLE: "Your Membership",
        EXTEND_BUTTON: "Extend",
        UPGRADE_BUTTON: "Upgrade",
        CRYPTO_INSTRUCTIONS: (
          <>
            If you would like to pay via crypto, please{" "}
            <a
              href="mailto:support@pulse-date.com"
              className="underline hover:text-gray-300"
            >
              contact support here
            </a>
            .
          </>
        ),
        ENDING_IN: "ending in",
        CANCEL_SUBSCRIPTION: "Cancel subscription",
        REMAIN_ACTIVE_UNTIL: "Your membership will remain active until",
        NO_RENEWAL: "It will not renew, and you won't be charged again.",
        CANCELED_SUCCESS: "Subscription canceled",
        MANAGE_SUBSCRIPTION: "Manage Subscription",
      },
      ERRORS: {
        MEMBERSHIP_LOADING:
          "There was a problem loading your membership. Please adjust your VPN or try again later.",
        SUBSCRIPTION_CANCELED: "Error cancelling subscription",
      },
    },
    EMAIL_SETTINGS: {
      LABELS: {
        UPDATE_EMAIL_TITLE: "Email",
        SEND_CODE_BUTTON: "Send code",
        ONE_TIME_CODE: "One-time password",
        EMAILED_CODE_SUCCESS: "Emailed verification code",
        EMAIL_UPDATED_SUCCESS: "Updated email address",
        NEW_EMAIL_PLACEHOLDER: "New email",
      },
      ERRORS: {
        EMAIL_UPDATE_CONFLICT: "User with this email already exists.",
        EMAIL_UPDATE_INVALID: "Invalid email address.",
        EMAIL_UPDATE_ERROR: "An error occurred. Please try again.",
      },
    },
    APP_INSTALL_DIALOG: {
      LABELS: {
        DIALOG_TITLE: "Add to Home Screen",
        APP_EXPERIENCE: "App Experience",
        NOTIFICATIONS: "Notifications",
        SAVED_LOGIN: "Saved Login",
        INSTRUCTIONS_BUTTON: "Instructions",
      },
    },
    APP_RELOAD_CARD: {
      LABELS: {
        TITLE: "Reload App",
        DESCRIPTION: "Clear cache",
      },
    },
    SETTINGS_FEEDBACK_CARD: {
      LABELS: {
        TITLE: "Have questions or feedback?",
        MESSAGE_FOUNDERS_BUTTON: "Message Founders",
        EMAIL_US: "Email us at",
        DELETE_ACCOUNT_BUTTON: "Delete account",
        DELETE_ACCOUNT_TITLE: "Confirm account deletion",
        DELETE_ACCOUNT_DESCRIPTION:
          "Deleting your account will erase all your data and is not reversible.",
        DELETE_ACCOUNT_CONFIRM: "Delete my data",
      },
      ERRORS: {
        DELETE_ACCOUNT: "Error deleting account",
      },
    },
    INSTALL_APP_INSTRUCTIONS: {
      LABELS: {
        TITLE: "Install App",
        DESCRIPTION:
          "Adding the app to your home screen provides a better experience, such as notifications and hiding the URL bar.",
        IOS_STEP_1: "1. Open in Safari, tap share button",
        IOS_STEP_2: "2. Add to home screen",
        IOS_STEP_3: "3. Confirm",
        IOS_26_STEP_1: "1. Open in Safari, tap more button",
        IOS_26_STEP_2: "2. Tap Share",
        IOS_26_STEP_3: "3. Add to home screen",
        IOS_26_STEP_4: "4. Confirm",
        ANDROID_STEP_1: "1. Open in Chrome, tap more button",
        ANDROID_STEP_2: "2. Add to home screen",
        ANDROID_STEP_2_DEBUG: "Don't see the button Add to Home Screen?",
        ANDROID_STEP_2_BUTTON: "Find tips here",
        ANDROID_STEP_3: "3. Confirm",
        ANDROID_TIPS_TITLE: "Add to Home Screen not showing",
        ANDROID_TIPS_DESCRIPTION: "Try some of these tips below",
        ANDROID_TIPS_LIST: (
          <>
            <ul className="list-disc pl-5 space-y-3">
              <li>
                ✅ <strong>Update Chrome</strong>
                <br />
                Make sure you&apos;re using the latest version of Chrome from
                the Play Store.
              </li>
              <li>
                🚫 <strong>Avoid Incognito Mode</strong>
                <br />
                Add to home screen prompts won&apos;t appear in Incognito tabs.
              </li>
              <li>
                📲{" "}
                <strong>Try a different launcher (e.g. Nova Launcher)</strong>
                <br />
                Some default launchers (especially on Xiaomi/Redmi/POCO)
                interfere with add to home screen or icon display.
                <br />
                Try{" "}
                <a
                  href="https://play.google.com/store/apps/details?id=com.teslacoilsw.launcher"
                  className="underline"
                >
                  Nova Launcher
                </a>{" "}
                and set it as default.
              </li>
              <li>
                🔄 <strong>Restart your phone</strong>
                <br />
                Restart your phone after trying the above steps.
              </li>
              <li>
                🌐 <strong>Try a different browser</strong>
                <br />
                Test with Samsung Internet or Firefox to confirm it&apos;s not
                Chrome-specific.
              </li>
            </ul>
          </>
        ),
      },
    },
    REFERRAL_PAGE: {
      LABELS: {
        TITLE: "Referral program",
        SHARE_YOUR_CODE: "Share your referral link",
        REFERRALS: "Referrals",
        FEMALE_REMAINING_REWARDS_TITLE: "Remaining referrals",
        MALE_EXPIRY_DATE_TITLE: "Valid until",
        MALE_REWARD: "$50 when he pays his first month's membership",
        MALE_EXPIRY_DATE: "January 31, 2026",
        MALE_REFERRAL: "Male referral",
        FEMALE_REFERRAL: "Female referral",
        EARNINGS: "Your earnings",
        FEMALE_REWARD: "$30 per 3 referrals who are approved to join",
        CLAIMING_TITLE: "Claiming Rewards",
        CLAIMING_INSTRUCTIONS: `
          If you are an active user, reach out to support via the platform
          settings page and use the in-app chat. If you are an applicant, reach
          out to support@pulse-date.com from your registered email.
        `,
      },
    },
    REFER_AND_EARN: {
      LABELS: {
        BUTTON_TITLE: "Invite",
        CARD_TITLE: "Invite someone special",
        DESCRIPTION:
          "Use the link below to invite someone who appreciates quality over quantity.",
        NO_GUARANTEE: "Note: referring users doesn't guarantee your admission.",
        SEE_HOW_BUTTON: "See How",
      },
    },
    REFERRAL_LIST: {
      LABELS: {
        MY_REFERRALS: "My Referrals",
        NO_REFERRALS_TITLE: "No referrals yet",
        NO_REFERRALS_DESCRIPTION: "Users you refer will appear here",
      },
      ERRORS: {
        LOADING_TITLE: "Error loading referrals",
        LOADING_DESCRIPTION: "There was a problem loading your referrals",
      },
    },
    REFERRAL_STEPS: {
      [ReferralStep.APPLYING]: "Applying",
      [ReferralStep.APPLIED]: "Applied",
      [ReferralStep.APPROVED]: "Approved",
      [ReferralStep.DENIED]: "Denied",
      [ReferralStep.SUBSCRIBED]: "Subscribed",
    },
    ERROR_PAGE: {
      LABELS: {
        TITLE: "Oops, something went wrong",
        GO_BACK: "Go back",
        TRY_AGAIN: "Try again",
      },
    },
    NOTIFICATIONS_DIALOG: {
      LABELS: {
        TITLE: "Notifications",
        DESCRIPTION: "Get notified when someone messages you.",
        DESCRIPTION_APPLIED: "Find out when you're approved.",
        ENABLE_BUTTON: "Enable",
        DISMISS_BUTTON: "Dismiss",
        DONT_SHOW_BUTTON: "Don't show again",
      },
    },
    PUSH_NOTIFICATIONS_SETTINGS: {
      LABELS: {
        TITLE: "Push Notifications",
        DESCRIPTION: "Notifications that show on your device outside the app.",
        ENABLED_STATUS: "Enabled",
        DISABLE_BUTTON: "Disable",
        ENABLE_BUTTON: "Enable",
        DISABLED_STATUS:
          "Notifications are disabled. Please enable them in your device settings.",
      },
    },
    SELECT_PLAN_CARD: {
      LABELS: {
        TITLE: "Premium Membership",
        DESCRIPTION: "Unlimited messages and search results.",
        MONTH: "m",
        WEEK: "w",
        MONTHS: "months",
        MONTHLY: "Monthly",
        CARD: "Card",
        CRYPTO: "Crypto",
        ONE_TIME: "One-time",
        RECURRING: "Recurring",
        ONE_TIME_PAYMENT: "One-time payment. No auto-renewal.",
        RECURRING_PAYMENT:
          "You'll be able to cancel your subscription anytime.",
        PAY: "Pay",
        PAY_WITH_CRYPTO: "Pay with crypto",
        AGREE_TO_TERMS: (
          <>
            By continuing you agree to our{" "}
            <a
              href="https://pulse-date.com/terms"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              terms and refund policy.
            </a>
          </>
        ),
        AGREE_TO_TERMS_AND_PRIVACY: (
          <>
            By continuing you agree to our{" "}
            <a
              href="https://pulse-date.com/terms"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              terms
            </a>{" "}
            and{" "}
            <a
              href="https://pulse-date.com/privacy"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              privacy policy.
            </a>
          </>
        ),
        INFO_HAVE_EXISTING_UNTIL:
          "You currently have a one-time plan active until",
        INFO_SWITCHING_IMMEDIATE:
          "Switching to a subscription plan will start immediately.",
        RESTORE_PURCHASES: "Restore Purchases",
        ACTIVATE: "Activate",
        TRIAL_PLAN_NAME: "7-Day Trial",
        TRIAL_FREE: "Free",
        TRIAL_DIALOG_TITLE: "Activate One-time Trial",
        TRIAL_DIALOG_DESCRIPTION:
          "This is a one-time only activation. Your free trial can only be activated once and can't be undone. Are you sure you want to continue?",
        TRIAL_ACTIVATED_SUCCESS: "Free trial activated",
        TRIAL_ACTIVATION_FAILED: "Couldn't activate free trial",
      },
      ERRORS: {
        LOADING: "Error loading subscription plans",
        NONE_AVAILABLE: "No subscription plans available",
      },
    },
    TRIAL_BANNER: {
      LABELS: {
        GIFTED: "You were gifted a free trial.",
        ACTIVATE: "Activate",
      },
    },
    PAYMENT_CONFIRMATION_DIALOG: {
      LABELS: {
        TITLE: "Did you complete the payment?",
        DESCRIPTION:
          "Please wait for the completion screen before marking done.",
        PAID_BUTTON: "I made the payment",
        NOT_NOW_BUTTON: "Not now",
        SUCCESS_MESSAGE: "Subscription updated",
        FAILURE_MESSAGE: "Unable to complete payment",
        FAILURE_DESCRIPTION:
          "Please try again using the same or a different method.",
        PURCHASES_RESTORED: "Purchases restored",
        NO_PREVIOUS_PURCHASE: "No previous purchase",
        FAILED_TO_CHECK_PURCHASE: "Failed to check purchase",
        PURCHASE_PROCESSING_MESSAGE: "Purchase received",
        PURCHASE_PROCESSING_DESCRIPTION:
          "We're confirming your purchase — your membership will update shortly.",
      },
    },
    OFFBOARDING_FORM: {
      LABELS: {
        WHY_LEAVING_TITLE: "Why are you leaving?",
        CHOOSE_REASON_DESCRIPTION: "Choose the main reason.",
        FOUND_ON_PULSE: "Found a relationship on Pulse",
        FOUND_ELSEWHERE: "Found a relationship elsewhere",
        TAKING_BREAK: "Taking a break from dating",
        NOT_ENOUGH_MESSAGES: "Not getting enough messages",
        TOO_EXPENSIVE: "Membership is too expensive",
        OTHER: "Other",
        MEMBERS_GROWING_TITLE: "Members are growing",
        MEMBERS_GROWING_DESCRIPTION_FEMALE: `Pulse launched recently and is growing every week, 
                  this will definitely improve over time. Men are also required to buy a
                  membership to message, which filters for those who are serious about
                  meeting and filters attention for you. 🌹`,
        MEMBERS_GROWING_DESCRIPTION_MALE: `Pulse launched recently and is growing every week, 
                  this will definitely improve over time. If there are any cities or
                  nationalities you'd like to see more, simply let us know
                  in a support message, we read all the feedback.`,
        CONTINUE_DELETING_BUTTON: "Continue deleting",
        MEMBERS_GETTING_VALUE_TITLE: "Members are getting value",
        MEMBERS_GETTING_VALUE_DESCRIPTION: `Pulse is a private member's club, not a mass-market dating
              app. A single subscription gives you direct access to message elegant, 
              vetted women, and is more economical than a night out. We hold the highest 
              profile standards in the space, and our members consistently tell us it's worth
              it. When the timing is right, we'd love to have you back.`,
        IN_A_FEW_WORDS_WHY_TITLE: "In a few words, tell us why",
        MIN_TEN_CHARS_PLACEHOLDER: "Min. 10 characters",
      },
    },
  },
  [PlatformLanguage.RUSSIAN]: {
    SHARED: {
      LABELS: {
        BACK_BUTTON: "Назад",
        CONTINUE_BUTTON: "Продолжить",
        SENDING_BUTTON: "Отправка...",
        SAVE_BUTTON: "Сохранить",
        CANCEL_BUTTON: "Отменить",
        EDIT_BUTTON: "Изменить",
        CONFIRM_BUTTON: "Подтвердить",
        DONE_BUTTON: "Готово",
        CONTACT_BUTTON: "Контакт",
        REFRESH_BUTTON: "Перезагрузить",
      },
      ERRORS: {
        PERSISTENT: `Пожалуйста, настройте ваш VPN или попробуйте снова позже. Если проблема сохраняется, свяжитесь с ${CONTACT.SUPPORT_EMAIL}`,
        TRY_AGAIN: "Произошла ошибка. Пожалуйста, попробуйте еще раз.",
      },
    },
    LOGIN_OTP_CARD: {
      LABELS: {
        LOGIN_CODE_TITLE: "Код входа",
        CHECK_EMAIL: "Проверьте email на одноразовый код.",
        SIGN_IN: "Войти",
        SIGNING_IN: "Вход...",
        RESEND_CODE: "Отправить код повторно",
        RESENDING: "Отправка...",
      },
    },
    LOGIN_EMAIL_CARD: {
      LABELS: {
        COMPLETE_CAPTCHA: "Пожалуйста, выполните капчу.",
        EMAIL: "Email",
        BUTTON_SENDING: "Отправка...",
        BUTTON_CONTINUE: "Продолжить",
        NO_ACCOUNT_QUESTION: "Нет аккаунта? ",
        HAVE_ACCOUNT_QUESTION: "Уже есть аккаунт? ",
        SIGN_UP: "Зарегистрироваться",
        SIGN_IN: "Войти",
        OR: "или",
        CONTINUE_WITH_GOOGLE: "Продолжить с Google",
        CREATING_NEW_ACCOUNT: "Новый e-mail найден, зарегистрируйтесь",
        LAST_USED: "Последнее",
      },
      ERRORS: {
        USER_ALREADY_EXISTS:
          "Пользователь уже существует. Пожалуйста, войдите.",
        INVALID_EMAIL: "Неверный email.",
        PLEASE_ENTER_VALID: "Введите действительный email.",
        GOOGLE_NOT_SUPPORTED_TITLE: "Вход через Google не поддерживается",
        GOOGLE_NOT_SUPPORTED_DESCRIPTION:
          "Yandex на iOS не поддерживает вход через Google, воспользуйтесь Safari, Chrome или другим способом.",
      },
    },
    MENU_BAR: {
      EXPLORE: "Обзор",
      AI_MATCHES: "ИИ подбор",
      SEARCH: "Поиск",
      FAVORITES: "Фавориты",
      MESSAGES: "Чаты",
    },
    PROFILE_MENU: {
      PROFILE: "Профиль",
      ACCOUNT: "Аккаунт",
      SIGN_OUT: "Выйти",
    },
    ONBOARDING_TERMS: {
      LABELS: {
        TERMS_TITLE: "Условия",
        TERMS_CONFIRMATION: (
          <>
            Я принимаю{" "}
            <a
              href="https://pulse-date.com/terms"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              условия
            </a>{" "}
            и{" "}
            <a
              href="https://pulse-date.com/privacy"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              политику конфиденциальности
            </a>
          </>
        ),
        AGE_CONFIRMATION: "Мне 18 лет или старше",
        LANGUAGE_PREFERENCE: "Язык",
        UNITS_PREFERENCE: "Единицы",
      },
      ERRORS: {
        ACCEPT_TERMS: "Ошибка при принятии условий и установке предпочтений",
        TERMS_CONFIRMATION_REQUIRED: "Вы должны принять условия и положения",
        AGE_CONFIRMATION_REQUIRED:
          "Вы должны подтвердить, что вам 18 или старше",
      },
    },
    ONBOARDING_STEPS: {
      LABELS: {
        FORM: "Анкета",
        PHOTOS: "Фото",
        FACE: "Лицо",
      },
    },
    GENDER_SELECT_CARD: {
      LABELS: {
        PROMPT: "Вы...",
        MALE: "Мужчина",
        FEMALE: "Девушка",
      },
    },
    USER_FORM: {
      LABELS: {
        TITLE: "Свои данные",
        OPTIONAL_DETAILS: "Необязательные поля",
        DISPLAY_NAME: "Никнейм",
        MAIN_LOCATION: "Главный город",
        DATE_OF_BIRTH: "Дата рождения",
        LANGUAGES: "Языки",
        NATIONALITY: "Национальность",
        ETHNICITY: "Этнос",
        OPEN_TO: "Открыт для",
        OPEN_TO_MIN: "Мин 1",
        SECONDARY_LOCATION: "Второй город",
        HEIGHT: "Рост",
        SMOKER: "Курение",
        TATTOOS: "Татуировки",
        DRINKING: "Алкоголь",
        YOUR_BIO: "О себе",
        REFERRED_BY: "Пригласил",
        CONTACT_INFO_TITLE: "Контактная информация",
        CONTACT_INFO_DESCRIPTION:
          "Эти необязательные поля останутся приватными, пока вы не поделитесь ими с другими пользователями. Вы сможете отправить их в одно касание.",
        UPGRADE_TO_EDIT: "Подпишитесь чтобы редактировать",
        CONTACT_INFO_SUSPEND:
          "Указание контактов в биографии заблокирует ваш аккаунт",
      },
      PLACEHOLDERS: {
        DISPLAY_NAME_FEMALE: "Alina123",
        DISPLAY_NAME_MALE: "John123",
        CITY_INPUT: "Dubai",
        LANGUAGE_INPUT: "English",
        NATIONALITY_INPUT: "American",
        ETHNICITY_INPUT: "Выбрать",
        BIO_INPUT:
          "30-250 символов, контактная информация запрещена. Что-то о вас или что вы ищете.",
        REFERRED_BY_INPUT: "Код или email",
      },
      ENUM_BADGES: {
        [OpenTo.DATES]: "Свидания",
        [OpenTo.RELATIONSHIP]: "Отношения",
        [OpenTo.TRAVEL_TOGETHER]: "Путешествия вместе",
        [SmokerStatus.NO]: "Нет",
        [SmokerStatus.SOMETIMES]: "Иногда",
        [SmokerStatus.REGULAR]: "Регулярно",
        [TattooStatus.NONE]: "Нет",
        [TattooStatus.MINIMAL]: "Минимально",
        [TattooStatus.MODERATE]: "Умеренно",
        [PlatformUnits.METRIC]: "см/км",
        [PlatformUnits.US]: "футы/мили",
      },
      ERRORS: {
        FORM_SUBMISSION: "Ошибка при отправке формы",
        SECONDARY_LOCATION_DIFFERENT: "Должен отличаться от главного города",
        CONTACT_INFO_NOT_ALLOWED_BIO:
          "Контактная информация не разрешена в биографии",
      },
    },
    USER_FORM_ACTION_REQUIRED: {
      LABELS: {
        TITLE: "Требуется действие",
        DESCRIPTION:
          "Мы рассмотрели вашу заявку, но вам нужно обновить форму в соответствии с нашими рекомендациями:",
        NO_CONTACT_INFO:
          "Имя и информация о себе не должны содержать контактов или ссылок",
        NO_OFFENSIVE_LANGUAGE:
          "В вашем имени или биографии не должно быть оскорбительной лексики",
      },
    },
    USER_FORM_SCHEMA_VALIDATION: {
      DISPLAY_NAME: {
        MIN: "Минимум 3 символа",
        MAX: "Максимум 20 символов",
        REGEX:
          "Только буквы, цифры и точки. Без пробелов или специальных символов.",
      },
      CITY: {
        MIN: "Выберите ваш город",
        MAX: "Выберите не более одного города",
      },
      LANGUAGES: {
        MIN: "Выберите минимум один язык",
        MAX: "Выберите не более 5 языков",
      },
      NATIONALITY: {
        MIN: "Выберите вашу национальность",
        MAX: "Выберите не более одной национальности",
      },
      ETHNICITY: {
        MIN: "Выберите вашу этническую принадлежность",
        MAX: "Выберите не более одной этнической принадлежности",
      },
      OPEN_TO: {
        MIN: "Выберите хотя бы один вариант",
      },
      BADGES: {
        MIN: "Выберите хотя бы один",
        MAX: "Выберите не более одного",
      },
      BIO: {
        MIN: "Минимум 30 символов",
        MAX: "Максимум 250 символов",
      },
      REFERRED_BY: {
        MIN: "Минимум 3 символа",
        MAX: "Максимум 20 символов",
        REGEX: "Только буквы, цифры и символы email. Без пробелов.",
      },
      TELEGRAM: {
        MIN: "Минимум 3 символа",
        MAX: "Максимум 40 символов",
        REGEX: "Разрешены только буквы, цифры и знаки подчеркивания",
      },
      INSTAGRAM: {
        MIN: "Минимум 1 символ",
        MAX: "Максимум 30 символов",
        REGEX: "Разрешены только буквы, цифры и знаки подчеркивания",
        DUPLICATE_PERIODS: "Точки не могут идти подряд",
        START_PERIODS: "Имя пользователя не может начинаться с точки",
        END_PERIODS: "Имя пользователя не может заканчиваться точкой",
      },
      WHATSAPP: {
        MIN: "Номер телефона должен содержать минимум 6 цифр",
        MAX: "Номер телефона не может быть длиннее 20 цифр",
        REGEX: "Может содержать только цифры и начинаться со знака '+'",
      },
    },
    ETHNICITIES: {
      [Ethnicity.ASIAN]: "Азиат",
      [Ethnicity.BLACK_AFRICAN_DESCENT]: "Чернокожий / Афроамериканец",
      [Ethnicity.LATIN_HISPANIC]: "Латиноамериканец / Испанец",
      [Ethnicity.MIDDLE_EASTERN]: "Ближневосточный",
      [Ethnicity.MIXED]: "Смешанный",
      [Ethnicity.SOUTH_ASIAN]: "Южноазиатский",
      [Ethnicity.WHITE]: "Белый",
      [Ethnicity.OTHER]: "Другое",
    },
    PHOTO_UPLOAD_PAGE: {
      LABELS: {
        CARD_TITLE: "Мои фото",
        DESCRIPTION:
          "Добавьте как минимум 3 фото. Последние 3 приватные фото необязательны и видны только если вы поделитесь ими.",
        MORE_INFO_FACE:
          "Хотя бы одно фото должно быть чётким снимком вашего лица, это может быть приватное фото.",
        MORE_INFO_FORBIDDEN: (
          <>
            <strong>Никаких</strong> групповых фото, обнажённости, контакты или
            оскорбительного контента.
          </>
        ),
        MORE_INFO_TIP: (
          <>
            <strong>Совет:</strong> загружайте разнообразные фото, чтобы
            увеличить свои шансы на получение совпадений.
          </>
        ),
        CROP_MODAL_TILE: "Обрезать фото",
        CROP_MODAL_WARNING: "Интимный контент и контакты запрещены.",
      },
      ERRORS: {
        UPLOAD: "Ошибка при загрузке фото",
        UPLOADS: "Ошибка при завершении загрузок",
        DELETE: "Ошибка при удалении фото",
        HEIC_CONVERSION:
          "Ошибка при конвертации фото HEIC/HEIF. Пожалуйста, попробуйте другое фото.",
        LOW_RESOLUTION_TITLE: "Фото низкого разрешения",
        LOW_RESOLUTION_DESCRIPTION: (resolution: string) => (
          <>
            Пожалуйста, выберите фото с минимальным разрешением{" "}
            <strong>{resolution}</strong> пикселей. Рекомендуем выбрать фото из
            вашей галереи, а не использовать скриншот, так как скриншоты часто
            имеют более низкое качество.
          </>
        ),
      },
    },
    PHOTO_UPLOAD_ACTION_REQUIRED: {
      TITLE: "Требуется действие",
      DESCRIPTION:
        "Мы рассмотрели вашу заявку, но вам нужно обновить ваши фото в соответствии с нашими правилами:",
      NO_FACE:
        "Хотя бы одно фото должно быть чётким снимком вашего лица, это может быть приватное фото",
      NO_FORBIDDEN:
        "Никаких групповых фото, обнажённости, контакты или оскорбительного контента",
    },
    PHOTO_UPLOAD_ACTION_REQUIRED_ELEGANT: {
      TITLE: "Требуется действие",
      DESCRIPTION: (
        <>
          Мы видим <b>потенциал</b> в вашем профиле, но одна или несколько из
          ваших <b>первых 3</b> фото нуждаются в обновлении. Мы приглашаем вас
          обновить свои фото:
        </>
      ),
      ELEGANT_PHOTOS: (
        <>
          <b className="text-white">элегантный и утонченный стиль</b> — Pulse
          акцентирует изысканность и элегантность, а не спортивный или
          повседневный стиль.
        </>
      ),
      HIGH_QUALITY_PHOTOS: (
        <>
          <b className="text-white">качество</b> — фото должны быть хорошего
          разрешения и четкими (без селфи в ванной, грязных зеркал и т. д.)
        </>
      ),
      HAPPY_TO_REVIEW: "Мы рады повторно рассмотреть вашу заявку.",
    },
    FACE_VERIFICATION: {
      LABELS: {
        TITLE: "Верификация лица",
        DESCRIPTION: (
          showFigure: boolean,
        ) => `Вам будет предложено записать короткое видео. 
        Пожалуйста, покажите своё лицо ${
          showFigure ? "и себя " : ""
        } как показано ниже.`,
        MORE_INFO: `Вы можете вернуться в любое время, чтобы завершить этот шаг. 
        Убедитесь, что вы находитесь в хорошо освещённом месте. 
        Все пользователи обязаны подтвердить своё лицо и живость.`,
        STEP_ONE: "1. Покажите своё лицо",
        STEP_TWO: "2. Покажите себя",
        PAGE_TWO_DESCRIPTION: "Запишите, как вы вращаете голову.",
        RECORD_BUTTON: "Записать",
        RETAKE_BUTTON: "Перезаписать",
        STOP_BUTTON: "Стоп",
        VERIFY_SAFARI:
          "Не видите камеру? Откройте в Safari, чтобы подтвердить свое лицо, затем вернитесь в приложение.",
      },
      ERRORS: {
        UPLOAD: "Не удалось загрузить видео",
        EXISTING: "Не удалось использовать существующее видео",
        CAMERA: (err: string) =>
          `Ошибка камеры: ${err}. Пожалуйста, проверьте разрешения для камеры.`,
      },
    },
    FACE_VERIFICATION_ACTON_REQUIRED: {
      TITLE: "Требуется действие",
      DESCRIPTION:
        "Мы рассмотрели вашу заявку, но вам нужно повторно отправить своё лицо:",
      CLARITY: "Убедитесь, что ваше лицо хорошо видно",
      SAME_ON_PHOTOS: "Убедитесь, что это вы на ваших фото",
    },
    APPLIED_PAGE: {
      TITLE: "Ваша заявка отправлена",
      DESCRIPTION:
        "Наша команда рассмотрит вашу заявку. Если вы будете одобрены, вы получите email в течение трех дней.",
      SECTION_HEADER: "Что Ожидать",
      FOLLOW_US_BUTTON: "Подписаться",
      NO_FAKES_TITLE: "Никаких фейков",
      NO_FAKES_DESCRIPTION:
        "Верификация лица обязательна для всех, кто хочет присоединиться к платформе. Если кто-то написал вам, значит, он прошёл верификацию.",
      SUCCESSFUL_MEN_TITLE: "Успешные мужчины",
      SUCCESSFUL_MEN_DESCRIPTION:
        "Мужчины платят несколько сотен долларов в месяц за членство. Это значит, что только те, кто может себе это позволить, смогут вам написать.",
      BEAUTIFUL_GIRLS_TITLE: "Красивые девушки",
      BEAUTIFUL_GIRLS_DESCRIPTION:
        "Мы проверяем каждого заявителя, чтобы поддерживать высокий стандарт для вас, тем самым экономя ваше время и усилия на поиск подходящей пары.",
      MESSAGE_INSTANTLY_TITLE: "Пишите сразу",
      MESSAGE_INSTANTLY_DESCRIPTION:
        "Свайпы — это медленно и утомительно. На Pulse вы можете начать общение с другими пользователями сразу и поделиться контактной информацией в одно касание.",
      TIME_SAVING_TITLE: "Экономия времени, удобные фильтры",
      TIME_SAVING_DESCRIPTION:
        "Ищите по нескольким местоположениям одновременно, национальности, языкам и другим фильтрам, которых нет в других приложениях для знакомств. Всё в несколько кликов.",
    },
    APPLIED_DOORS_CLOSED: {
      TITLE: "Ваша заявка отправлена",
      DESCRIPTION:
        "Сейчас мы формируем самое первое сообщество участников. Мы сообщим вам, когда всё будет готово к старту.",
    },
    WAITLISTED_PAGE: {
      TITLE: "Вы были внесены в список ожидания",
      DESCRIPTION:
        "Наша команда рассмотрела вашу заявку и добавила вас в список ожидания. Мы учтём её, когда появятся новые места. Если мы сможем предоставить вам доступ, мы свяжемся с вами по email.",
    },
    DENIED_PAGE: {
      TITLE: "Приглашаем вас подать заявку повторно",
      DESCRIPTION: `Pulse является очень избирательным, и количество мест ограничено. 
                    Большинство пользователей не проходят с первого раза.
                    Хотя в настоящее время мы не можем предоставить вам доступ, ситуация 
                    может измениться. Вы можете повторно подать заявку через 180 дней с 
                    момента подачи первой заявки.`,
    },
    BANNED_PAGE: {
      TITLE: "Ваш аккаунт заблокирован.",
      REASON_PAYMENT: `Мы приостановили ваш аккаунт из-за проблемы с оплатой. Если вы хотите восстановить аккаунт, вы можете сделать это, погасив задолженность. Пожалуйста, свяжитесь с ${CONTACT.SUPPORT_EMAIL} для получения инструкций.`,
      REASON_VIOLATION:
        "Ваш аккаунт был приостановлен из-за нарушения условий и правил нашей платформы.",
    },
    SEARCH_PAGE: {
      LABELS: {
        SEARCH_BUTTON: "Поиск",
        CLEAR_BUTTON: "Очистить",
        TITLE: "Фильтры",
        TITLE_MOBILE: "Фильтры поиска",
        LOCATION: "Город",
        DISTANCE: "Расстояние",
        AGE: "Возраст",
        LANGUAGES: "Языки",
        NATIONALITY: "Национальность",
        ETHNICITY: "Этнос",
        HEIGHT: "Рост",
        OPEN_TO: "Открыт для",
        SMOKER: "Курение",
        TATTOOS: "Татуировки",
        DRINKING: "Алкоголь",
      },
      PLACEHOLDERS: {
        CITIES_INPUT: "Dubai",
        LANGUAGES_INPUT: "Говорит на одном из, поиск (англ.)",
        NATIONALITY_INPUT: "Russian",
        ETHNICITY_INPUT: "Выбрать",
      },
      SORT_BY: {
        RECENTLY_ACTIVE: "Недавно активные",
        NEWEST: "Новые",
      },
      ERRORS: {
        SEARCH: "Ошибка при поиске профилей",
        SEARCH_DESCRIPTION:
          "Возникла проблема с поиском. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
        NO_RESULTS: "Нет результатов для поиска",
        NO_RESULTS_DESCRIPTION: "Попробуйте изменить фильтры.",
      },
    },
    PHOTO_SHARING: {
      LABELS: {
        SHARE_SUCCESS: "Отправил(а) приватные фото",
        UNSHARE_SUCCESS: "Скрыл(a) приватные фото",
        SHARED_MESSAGE: (name) =>
          `${name ? `${name} отправил(а)` : "Вы отправили"} приватные фото`,
        REQUESTED_MESSAGE: (name) =>
          `${name ? `${name} запросил(а)` : "Вы запросили"} приватные фото`,
        SHARE_BUTTON: "Поделиться",
        UNSHARE_BUTTON: "Скрыть",
        HAS_PRIVATE_PHOTOS: "У этого пользователя есть приватные фото.",
        REQUEST_BUTTON: "Запросить",
        REQUESTED_BUTTON: "Запрошено",
        NO_IMAGES_AVAILABLE: "Нет фото",
      },
      ERRORS: {
        SHARE: "Ошибка при отправке фото",
        UNSHARE: "Ошибка при удалении фото",
        REQUESTING: "Ошибка при запросе фото",
      },
    },
    PROFILE_VIEW: {
      LABELS: {
        BLOCK_BUTTON: "Заблокировать",
        REPORT_BUTTON: "Пожаловаться",
        REPORT_DIALOG_TITLE: "Пожаловаться на пользователя",
        REPORT_DIALOG_DESCRIPTION:
          "Пожалуйста, укажите причину, почему вы жалуетесь на пользователя.",
        REPORT_USER_BUTTON: "Пожаловаться",
        REPORT_SUCCESS: "Жалоба отправлена",
        VIEW_CHAT_BUTTON: "Чат",
        MESSAGE_BUTTON: "Написать",
        GENERAL_SECTION: "Основное",
        LANGUAGES_SECTION: "Языки",
        OPEN_TO: "Открыт для",
        SMOKER: "Курение",
        TATTOOS: "Татуировки",
        DRINKING: "Алкоголь",
        LANGUAGES: "Языки",
        JOINED_ON: "Присоединение",
      },
      ERRORS: {
        LOADING: "Ошибка загрузки профиля",
        LOADING_DESCRIPTION:
          "Возникла проблема с загрузкой профиля. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
        BLOCK: "Ошибка при блокировке пользователя",
        REPORT: "Ошибка при отправке жалобы на пользователя",
      },
    },
    SELF_PROFILE_VIEW: {
      LABELS: {
        YOUR_PROFILE: "Мой профиль",
        EDIT_BUTTON: "Изменить",
        EDIT: "Изменить профиль",
        EDIT_SUCCESS: "Профиль обновлён",
        VIEW_BUTTON: "Посмотреть",
      },
      ERRORS: {
        LOADING: "Ошибка загрузки профиля",
        LOADING_DESCRIPTION:
          "Возникла проблема с загрузкой профиля. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
      },
    },
    MESSAGE_SHORTCUT: {
      LABELS: {
        TITLE: "Готовое сообщение",
        DESCRIPTION:
          "Настройте короткое сообщение для отправки одним нажатием.",
        INPUT_PLACEHOLDER: "Введите сообщение (макс. 120 символов)",
        CLEAR_BUTTON: "Очистить",
      },
      ERROR: {
        UPDATE: "Не удалось обновить быстрое сообщение",
      },
    },
    MESSAGE_DRAWER: {
      LABELS: {
        SEND_BUTTON: "Отправить",
        UPGRADE: "Подписаться",
        UPGRADE_PLACEHOLDER: (name) => `Подпишитесь, чтобы написать ${name}...`,
        MESSAGE_PLACEHOLDER: (name) => `Написать ${name}...`,
      },
      ERROR: {
        SEND: "Не удалось отправить сообщение пользователю",
        MEMBERSHIP:
          "Произошла ошибка при загрузке вашей подписки. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
        MESSAGE_EMPTY: "Сообщение не может быть пустым",
        MESSAGE_TOO_LONG: "Сообщение не может превышать 250 символов",
      },
    },
    FAVORITES_PAGE: {
      LABELS: {
        FAVORITES: "Фавориты",
        FAVORITED_ME: "Меня лайкнули",
        LAST_FAVORITED: "Последние фавориты",
        RECENTLY_ACTIVE: "Недавно активные",
        MY_FAVORITES_EMPTY: "Пока нет фаворитов",
        MY_FAVORITES_EMPTY_DESCRIPTION:
          "Когда вы добавите профили в фавориты, они появятся здесь для быстрого доступа.",
        FAVORITED_ME_EMPTY: "Вас пока никто не лайкнул",
        FAVORITED_ME_EMPTY_DESCRIPTION:
          "Когда кто-то добавит вас в фавориты, они появятся здесь.",
      },
      ERRORS: {
        LOADING: "Ошибка загрузки фаворитов",
        LOADING_DESCRIPTION:
          "Произошла проблема при загрузке ваших фаворитов. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
        COULD_NOT_FAVORITE: "Ошибка добавления в фавориты",
        COULD_NOT_UNFAVORITE: "Ошибка удаления из фаворитов",
      },
    },
    MESSAGES: {
      LABELS: {
        HIDDEN: "Скрыто",
        NO_CONVERSATIONS_TITLE: "Нет сообщений",
        NO_CONVERSATIONS_DESCRIPTION: "У вас пока нет сообщений.",
        ACTIVE: "был(а)",
        DELETE_BUTTON: "Удалить",
        UPGRADE_TO_READ_MESSAGE: "Подпишитесь, чтобы читать и писать",
        MESSAGE: "Написать",
        COPY_BUTTON: "Копировать",
        CONFIGURE_CONTACTS: "Настроить контакты",
        SELECT_TO_START: "Выберите чат для сообщений",
      },
      ERRORS: {
        DELETE: "Ошибка при удалении сообщения",
        DELETE_CONVERSATION: "Ошибка при удалении сообщений",
        SEND: "Не удалось отправить сообщение",
        LOADING: "Ошибка при загрузке сообщений",
        LOADING_DESCRIPTION:
          "Возникла проблема с загрузкой ваших сообщений. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
      },
    },
    EXPLORE_PAGE: {
      LABELS: {
        EMPTY_TITLE: "Нет профилей для просмотра",
        EMPTY_DESCRIPTION:
          "Заходите позже, чтобы увидеть новый контент от пользователей.",
      },
      ERRORS: {
        LOADING: "Ошибка при загрузке профилей",
        LOADING_DESCRIPTION:
          "Возникла проблема с загрузкой профилей. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
        REFRESH: "Ошибка при обновлении профилей",
      },
    },
    ACCOUNT_SETTINGS: {
      LABELS: {
        REFERRAL_CARD_TITLE: "Пригласите кого-то особенного",
        REFERRAL_CARD_DESCRIPTION:
          "Используйте ссылку ниже, чтобы пригласить человека, который ценит качество, а не количество.",
        UPDATED_SUCCESS: "Обновлены настройки аккаунта",
        GENERAL_SETTINGS_SECTION_TITLE: "Настройки аккаунта",
        NOTIFICATIONS_SECTION_TITLE: "Почта",
        EMAIL_NOTIFICATIONS: "Уведомления о новых сообщениях",
        LANGUAGE_SETTING: "Язык",
        UNITS_SETTING: "Единицы измерения",
      },
      ERRORS: {
        LOADING_TITLE: "Ошибка при загрузке настроек аккаунта",
        LOADING_DESCRIPTION:
          "Возникла проблема с загрузкой настроек вашего аккаунта. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
        UPDATED_ERROR: "Ошибка при обновлении настроек аккаунта",
      },
    },
    MEMBERSHIP_SETTINGS: {
      LABELS: {
        FREE_PREVIEW_TITLE: "Бесплатный план",
        FREE_PREVIEW_DESCRIPTION: (
          <>
            Подпишитесь на <strong>полный план</strong>, чтобы начать общение с
            другими пользователями.
          </>
        ),

        FULL_MEMBER_TITLE: "Полный план",
        FULL_MEMBER_DESCRIPTION: (date) => (
          <>
            Ваша подписка закончится <strong>{date}</strong>
          </>
        ),
        FULL_MEMBER_RECURRING_DESCRIPTION: (date) => (
          <>
            Ваша подписка будет продлена в <strong>{date}</strong>
          </>
        ),
        FULL_MEMBER_CANCELLED_DESCRIPTION: (date) => (
          <>
            Ваша подписка закончится <strong>{date}</strong>
          </>
        ),
        MEMBERSHIP_ENDED_TITLE: "Закончился",
        MEMBERSHIP_ENDED_DESCRIPTION: (date) => (
          <>
            Ваша подписка закончилась <strong>{date}</strong> Подпишитесь, чтобы
            продолжить общение с другими пользователями.
          </>
        ),
        YOUR_MEMBERSHIP_TITLE: "Ваша подписка",
        EXTEND_BUTTON: "Продлить",
        UPGRADE_BUTTON: "Подписаться",
        CRYPTO_INSTRUCTIONS: (
          <>
            Если вы хотите оплатить через криптовалюту, пожалуйста,{" "}
            <a
              href="mailto:support@pulse-date.com"
              className="underline hover:text-gray-300"
            >
              свяжитесь с поддержкой здесь
            </a>
            .
          </>
        ),
        ENDING_IN: "заканчивающаяся на",
        CANCEL_SUBSCRIPTION: "Отменить подписку",
        REMAIN_ACTIVE_UNTIL: "Ваш план будет активен до",
        NO_RENEWAL: "без автопродления.",
        CANCELED_SUCCESS: "Подписка отменена",
        MANAGE_SUBSCRIPTION: "Настроить подписку",
      },
      ERRORS: {
        MEMBERSHIP_LOADING:
          "Возникла проблема с загрузкой вашей подписки. Пожалуйста, настройте ваш VPN или попробуйте снова позже.",
        SUBSCRIPTION_CANCELED: "Ошибка при отмене подписки",
      },
    },
    EMAIL_SETTINGS: {
      LABELS: {
        UPDATE_EMAIL_TITLE: "Почта",
        SEND_CODE_BUTTON: "Отправить код",
        ONE_TIME_CODE: "Одноразовый пароль",
        EMAILED_CODE_SUCCESS: "Код подтверждения отправлен на почту",
        EMAIL_UPDATED_SUCCESS: "Адрес почты обновлен",
        NEW_EMAIL_PLACEHOLDER: "Новый email",
      },
      ERRORS: {
        EMAIL_UPDATE_CONFLICT: "Пользователь с таким email уже существует.",
        EMAIL_UPDATE_INVALID: "Неверный адрес почты.",
        EMAIL_UPDATE_ERROR: "Произошла ошибка. Пожалуйста, попробуйте снова.",
      },
    },
    APP_INSTALL_DIALOG: {
      LABELS: {
        DIALOG_TITLE: "Установить приложение",
        APP_EXPERIENCE: "Работает как приложение",
        NOTIFICATIONS: "Уведомления",
        SAVED_LOGIN: "Сохраненный логин",
        INSTRUCTIONS_BUTTON: "Инструкция",
      },
    },
    APP_RELOAD_CARD: {
      LABELS: {
        TITLE: "Перезагрузить",
        DESCRIPTION: "Очистить кэш",
      },
    },
    SETTINGS_FEEDBACK_CARD: {
      LABELS: {
        TITLE: "Есть вопросы или предложения?",
        MESSAGE_FOUNDERS_BUTTON: "Написать основателям",
        EMAIL_US: "Напишите нам на",
        DELETE_ACCOUNT_BUTTON: "Удалить аккаунт",
        DELETE_ACCOUNT_TITLE: "Подтверждение удаления аккаунта",
        DELETE_ACCOUNT_DESCRIPTION:
          "Удаление вашего аккаунта приведет к стиранию всех ваших данных без возможности восстановления.",
        DELETE_ACCOUNT_CONFIRM: "Удалить мои данные",
      },
      ERRORS: {
        DELETE_ACCOUNT: "Ошибка при удалении аккаунта",
      },
    },
    INSTALL_APP_INSTRUCTIONS: {
      LABELS: {
        TITLE: "Установить приложение",
        DESCRIPTION:
          "Добавление приложения на главный экран улучшает опыт использования, включая уведомления и скрытие строки URL.",
        IOS_STEP_1: "1. Откройте в Safari, нажмите кнопку «Поделиться»",
        IOS_STEP_2: "2. Добавить на главный экран",
        IOS_STEP_3: "3. Подтвердить",
        IOS_26_STEP_1: "1. Откройте в Safari, нажмите кнопку «Ещё»",
        IOS_26_STEP_2: "2. Нажмите кнопку «Поделиться»",
        IOS_26_STEP_3: "3. Добавить на главный экран",
        IOS_26_STEP_4: "4. Подтвердить",
        ANDROID_STEP_1: "1. Откройте в Chrome, нажмите кнопку «Ещё»",
        ANDROID_STEP_2: "2. Добавить на главный экран",
        ANDROID_STEP_2_DEBUG: "Не видите кнопку «Добавить на главный экран»?",
        ANDROID_STEP_2_BUTTON: "Найдите советы здесь",
        ANDROID_STEP_3: "3. Подтвердить",
        ANDROID_TIPS_TITLE: "Добавить на главный экран не отображается",
        ANDROID_TIPS_DESCRIPTION:
          "Попробуйте некоторые из приведенных ниже советов",
        ANDROID_TIPS_LIST: (
          <>
            <ul className="list-disc pl-5 space-y-3">
              <li>
                ✅ <strong>Обновите Chrome</strong>
                <br />
                Убедитесь, что вы используете последнюю версию Chrome из Play
                Store.
              </li>
              <li>
                🚫 <strong>Избегайте режима инкогнито</strong>
                <br />
                Запросы на добавление на главный экран не появляются во вкладках
                инкогнито.
              </li>
              <li>
                📲{" "}
                <strong>
                  Попробуйте другой лаунчер (например, Nova Launcher)
                </strong>
                <br />
                Некоторые стандартные лаунчеры (особенно на Xiaomi/Redmi/POCO)
                мешают добавлению на главный экран или отображению иконок.
                <br />
                Попробуйте{" "}
                <a
                  href="https://play.google.com/store/apps/details?id=com.teslacoilsw.launcher"
                  className="underline"
                >
                  Nova Launcher
                </a>{" "}
                и установите его по умолчанию.
              </li>
              <li>
                🔄 <strong>Перезагрузите телефон</strong>
                <br />
                Перезагрузите телефон после выполнения вышеуказанных шагов.
              </li>
              <li>
                🌐 <strong>Попробуйте другой браузер</strong>
                <br />
                Протестируйте с Samsung Internet или Firefox, чтобы убедиться,
                что проблема не связана с Chrome.
              </li>
            </ul>
          </>
        ),
      },
    },
    REFERRAL_PAGE: {
      LABELS: {
        TITLE: "Реферальная программа",
        SHARE_YOUR_CODE: "Поделитесь реферальной ссылкой",
        REFERRALS: "Рефералы",
        FEMALE_REMAINING_REWARDS_TITLE: "Оставшиеся рефералы",
        MALE_REWARD: "$50 когда он оплатит первый месяц подписки",
        MALE_EXPIRY_DATE_TITLE: "До",
        MALE_EXPIRY_DATE: "31 января 2026 года",
        MALE_REFERRAL: "Мужской реферал",
        FEMALE_REFERRAL: "Женский реферал",
        EARNINGS: "Ваш заработок",
        FEMALE_REWARD:
          "$30 долларов за 3 приглашённых девушек, которые получат одобрение на участие",
        CLAIMING_TITLE: "Получение Наград",
        CLAIMING_INSTRUCTIONS: `
          Если вы активный пользователь, обратитесь в поддержку через страницу настроек
          платформы и используйте встроенный чат. Если вы заявитель, напишите на
          support@pulse-date.com с вашей зарегистрированной электронной почты.`,
      },
    },
    REFER_AND_EARN: {
      LABELS: {
        BUTTON_TITLE: "Пригласить",
        CARD_TITLE: "Пригласите кого-то особенного",
        DESCRIPTION:
          "Используйте ссылку ниже, чтобы пригласить человека, который ценит качество, а не количество.",
        NO_GUARANTEE:
          "Примечание: приглашение других пользователей не гарантирует одобрение вашей заявки.",
        SEE_HOW_BUTTON: "Узнать больше",
      },
    },
    REFERRAL_LIST: {
      LABELS: {
        MY_REFERRALS: "Мои Рефералы",
        NO_REFERRALS_TITLE: "Пока нет рефералов",
        NO_REFERRALS_DESCRIPTION:
          "Пользователи, которых вы пригласили, появятся здесь",
      },
      ERRORS: {
        LOADING_TITLE: "Ошибка загрузки рефералов",
        LOADING_DESCRIPTION: "Возникла проблема при загрузке ваших рефералов",
      },
    },
    REFERRAL_STEPS: {
      [ReferralStep.APPLYING]: "Заявка",
      [ReferralStep.APPLIED]: "Подана",
      [ReferralStep.APPROVED]: "Одобрена",
      [ReferralStep.DENIED]: "Отклонена",
      [ReferralStep.SUBSCRIBED]: "Подписан",
    },
    ERROR_PAGE: {
      LABELS: {
        TITLE: "Что-то пошло не так",
        GO_BACK: "Вернуться назад",
        TRY_AGAIN: "Попробовать снова",
      },
    },
    NOTIFICATIONS_DIALOG: {
      LABELS: {
        TITLE: "Уведомления",
        DESCRIPTION: "Узнайте, когда кто-то отправляет вам сообщение.",
        DESCRIPTION_APPLIED: "Узнайте, когда вас одобрят.",
        ENABLE_BUTTON: "Включить",
        DISMISS_BUTTON: "Закрыть",
        DONT_SHOW_BUTTON: "Больше не показывать",
      },
    },
    PUSH_NOTIFICATIONS_SETTINGS: {
      LABELS: {
        TITLE: "Push-уведомления",
        DESCRIPTION:
          "Уведомления, которые отображаются на вашем устройстве вне приложения.",
        ENABLED_STATUS: "Включены",
        DISABLE_BUTTON: "Отключить",
        ENABLE_BUTTON: "Включить",
        DISABLED_STATUS:
          "Уведомления отключены. Пожалуйста, включите их в настройках вашего аппарата.",
      },
    },
    SELECT_PLAN_CARD: {
      LABELS: {
        TITLE: "Премиум подписка",
        DESCRIPTION: "Неограниченные сообщения и результаты поиска.",
        MONTH: "м",
        WEEK: "н",
        MONTHS: "месяцев",
        MONTHLY: "Ежемесячно",
        CARD: "Карта",
        CRYPTO: "Крипто",
        ONE_TIME: "Одноразовый",
        RECURRING: "Периодический",
        ONE_TIME_PAYMENT: "Единовременный платеж. Без автопродления.",
        RECURRING_PAYMENT: "Вы сможете отменить свою подписку в любое время.",
        PAY: "Оплатить",
        PAY_WITH_CRYPTO: "Оплатить криптой",
        AGREE_TO_TERMS: (
          <>
            Продолжая, вы соглашаетесь с нашими{" "}
            <a
              href="https://pulse-date.com/terms"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              условиями и политикой возврата средств.
            </a>
          </>
        ),
        AGREE_TO_TERMS_AND_PRIVACY: (
          <>
            Продолжая, вы соглашаетесь с нашими{" "}
            <a
              href="https://pulse-date.com/terms"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              условиями
            </a>{" "}
            и{" "}
            <a
              href="https://pulse-date.com/privacy"
              target="_blank"
              className="underline"
              rel="noopener noreferrer"
            >
              политику конфиденциальности.
            </a>
          </>
        ),
        INFO_HAVE_EXISTING_UNTIL: "У вас уже есть разовый план, действующий до",
        INFO_SWITCHING_IMMEDIATE:
          "Переход на ежемесячный план начинается сразу.",
        RESTORE_PURCHASES: "Восстановить покупки",
        ACTIVATE: "Активировать",
        TRIAL_PLAN_NAME: "7-дней",
        TRIAL_FREE: "Бесплатно",
        TRIAL_DIALOG_TITLE: "Активировать одноразовый период",
        TRIAL_DIALOG_DESCRIPTION:
          "Это одноразовая активация. Бесплатный период можно активировать только один раз, и невозможно отменить. Вы уверены, что хотите продолжить?",
        TRIAL_ACTIVATED_SUCCESS: "Бесплатный период активирован",
        TRIAL_ACTIVATION_FAILED: "Не удалось активировать бесплатный период",
      },
      ERRORS: {
        LOADING: "Ошибка загрузки планов подписки",
        NONE_AVAILABLE: "Нет доступных планов подписки",
      },
    },
    TRIAL_BANNER: {
      LABELS: {
        GIFTED: "Вам подарили бесплатный период.",
        ACTIVATE: "Активировать",
      },
    },
    PAYMENT_CONFIRMATION_DIALOG: {
      LABELS: {
        TITLE: "Вы завершили оплату?",
        DESCRIPTION:
          "Пожалуйста, дождитесь экрана завершения, прежде чем отметить как выполненное.",
        PAID_BUTTON: "Я произвел оплату",
        NOT_NOW_BUTTON: "Не сейчас",
        SUCCESS_MESSAGE: "Подписка обновлена",
        FAILURE_MESSAGE: "Не удалось завершить платеж",
        FAILURE_DESCRIPTION:
          "Пожалуйста, повторите попытку, используя тот же или другой метод.",
        PURCHASES_RESTORED: "Покупки восстановлены",
        NO_PREVIOUS_PURCHASE: "Предыдущих покупок нет",
        FAILED_TO_CHECK_PURCHASE: "Не удалось проверить покупку",
        PURCHASE_PROCESSING_MESSAGE: "Покупка получена",
        PURCHASE_PROCESSING_DESCRIPTION:
          "Мы подтверждаем вашу покупку — статус подписки скоро обновится.",
      },
    },
    OFFBOARDING_FORM: {
      LABELS: {
        WHY_LEAVING_TITLE: "Почему вы нас покидаете?",
        CHOOSE_REASON_DESCRIPTION: "Выберите основную причину.",
        FOUND_ON_PULSE: "Нашел пару на Pulse",
        FOUND_ELSEWHERE: "Нашел пару в другом месте",
        TAKING_BREAK: "Хочу перерыв в знакомствах",
        NOT_ENOUGH_MESSAGES: "Получаю мало сообщений",
        TOO_EXPENSIVE: "Подписка слишком дорогая",
        OTHER: "Другое",
        MEMBERS_GROWING_TITLE: "Число пользователей растёт",
        MEMBERS_GROWING_DESCRIPTION_FEMALE: `Pulse недавно запущен и с каждой неделей набирает популярность, 
                  поэтому со временем ситуация обязательно улучшится. Мужчинам также необходимо купить
                  подписку, чтобы отправлять сообщения, что позволяет отсеять тех, кто не настроен на
                  серьезные отношения, и фильтрует внимание к вам. 🌹`,
        MEMBERS_GROWING_DESCRIPTION_MALE: `Pulse недавно запущен и с каждой неделей набирает популярность, 
                  поэтому со временем ситуация обязательно улучшится. Если есть города или
                  национальности, которых вы бы хотели видеть больше, просто сообщите нам об этом
                  в поддержке, мы читаем все сообщения.`,
        CONTINUE_DELETING_BUTTON: "Продолжить удаление",
        MEMBERS_GETTING_VALUE_TITLE: "Подписчики довольны",
        MEMBERS_GETTING_VALUE_DESCRIPTION: `Pulse - приватный клуб, а не массовое приложение для знакомств.
              Подписка предоставляет вам прямой доступ к общению с элегантными, 
              проверенными девушками и обходится дешевле, чем вечер в ресторане или клубе. Мы придерживаемся самых высоких 
              стандартов в этой сфере, и наши подписчики постоянно подтверждают, что это того стоит.
              Когда наступит подходящий момент, мы будем рады видеть вас снова.`,
        IN_A_FEW_WORDS_WHY_TITLE: "В пару слов расскажите нам, почему",
        MIN_TEN_CHARS_PLACEHOLDER: "Не менее 10 букв",
      },
    },
  },
};
